import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { flushSync } from "react-dom";
import {
  Box,
  VStack,
  Text,
  Button,
  Flex,
  Input,
  useToast,
  IconButton,
  Icon,
  Heading,
  HStack,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Divider,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Portal,
  SimpleGrid,
  Radio,
  RadioGroup,
  Tooltip,
  Checkbox,
  Switch,
  Textarea,
  Container,
  Card,
  CardBody,
  CardHeader,
  Link,
} from "@chakra-ui/react";
import { CheckIcon, ChevronDownIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import {
  FiChevronRight,
  FiFolder,
  FiList,
  FiMic,
  FiMove,
  FiVolumeX,
  FiTrendingUp,
  FiRefreshCw,
  FiShare2,
  FiPaperclip,
} from "react-icons/fi";
import { hitBeApi, clearClientApiCache } from "../../../api/api";
import TabCacheButton from "../../common/TabCacheButton";
import { VoiceAssistantMicButton } from "../../common/VoiceAssistantMicButton";
import ShareCollaborationModal from "../../common/ShareCollaborationModal";
import {
  getSpeechRecognitionClass,
  getPreferredSttLang,
  startBrowserVoiceDictation,
  VOICE_DICTATION_SILENCE_MS,
} from "../../../utils/browserSpeechRecognition";
import {
  cleanupPortfolioSpeechTranscript,
  PORTFOLIO_STT_LANG_OPTIONS,
  setPreferredSttLang,
} from "../../../utils/speechTranscriptCleanup";
import { speakBrowserAssistantReply } from "../../../utils/browserSpeechSpeak";
import { useNavigate } from "react-router-dom";
import DeleteButtonWithConfirmation from "../utils/DeleteConfirmation";
import axios from "axios";
import { getCookieToken } from "../../../utils/utils";
import { serverBaseUrl } from "../../../constants/Constants";
import { downloadPortfolioAnalyticsPdf } from "../../../utils/portfolioAnalyticsPdf";
import {
  savePortfolioReport,
  listPortfolioReportsForRoot,
  deletePortfolioReport,
  openPdfBlobInNewTab,
  downloadPdfBlob,
} from "../../../utils/savedPortfolioReportsIDB";
import { appStore } from "../../../store/Store";
import { format, parse } from "date-fns";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

function portfolioYmdIST(d) {
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return x.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function portfolioSubtractIstDays(ymd, n) {
  const d = new Date(`${ymd}T12:00:00+05:30`);
  d.setTime(d.getTime() - n * 86400000);
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function portfolioDefaultRangeLastDaysIst(days) {
  const end = portfolioYmdIST(new Date());
  const start = portfolioSubtractIstDays(end, Math.max(days - 1, 0));
  return { start, end };
}

const PORTFOLIO_ANALYTICS_RANGES = [
  { id: "30d", label: "30 days", days: 30 },
  { id: "60d", label: "60 days", days: 60 },
  { id: "90d", label: "90 days", days: 90 },
  { id: "6m", label: "6 months", days: 180 },
  { id: "all", label: "All time", days: null },
  { id: "custom", label: "Custom", days: null },
];

function formatInr(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : "0";
}

function chartXAxisIntervalByLength(len) {
  if (!len || len <= 0) return "preserveStartEnd";
  if (len <= 16) return 0;
  if (len <= 45) return 1;
  if (len <= 90) return 2;
  if (len <= 180) return 4;
  return 6;
}

function chartWidthForPoints(points, baseWidth) {
  if (!points || points <= 0) return baseWidth;
  const pxPerPoint =
    points > 900 ? 6 : points > 600 ? 7 : points > 360 ? 8 : points > 180 ? 10 : 12;
  return Math.max(baseWidth, points * pxPerPoint);
}

function chartHeightForPoints(points, baseHeight, maxHeight = 820) {
  if (!points || points <= 0) return baseHeight;
  const extra = Math.min(maxHeight - baseHeight, Math.floor(points / 3));
  return Math.max(baseHeight, Math.min(maxHeight, baseHeight + extra));
}

function portfolioDateFromYmd(ymd) {
  if (!ymd) return null;
  const d = new Date(`${ymd}T12:00:00+05:30`);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function portfolioDaysBetweenYmd(start, end) {
  const s = portfolioDateFromYmd(start);
  const e = portfolioDateFromYmd(end);
  if (!s || !e) return null;
  return Math.floor((e.getTime() - s.getTime()) / 86400000) + 1;
}

function portfolioAnalyticsBucketDays(rangeId, rangeQuery) {
  if (rangeId === "30d" || rangeId === "60d") return 1;
  if (rangeId === "90d") return 2;
  if (rangeId === "6m") return 5;
  if (rangeId === "all") return 10;
  if (rangeId === "custom") {
    const days = portfolioDaysBetweenYmd(rangeQuery?.start, rangeQuery?.end);
    if (!days) return 1;
    if (days <= 60) return 1;
    if (days <= 90) return 2;
    if (days <= 365) return 5;
    return 10;
  }
  return 1;
}

function resolvePortfolioSeriesRange(rangeQuery, series) {
  let start = String(rangeQuery?.start || "").trim();
  let end = String(rangeQuery?.end || "").trim();
  if (start && end) return { start, end };
  const src = Array.isArray(series) ? series : [];
  let min = null;
  let max = null;
  src.forEach((row) => {
    const day = row?.day || row?.date;
    const d = portfolioDateFromYmd(day);
    if (!d) return;
    const ms = d.getTime();
    if (min == null || ms < min) min = ms;
    if (max == null || ms > max) max = ms;
  });
  if (min == null || max == null) return { start: "", end: "" };
  return {
    start: portfolioYmdIST(new Date(min)),
    end: portfolioYmdIST(new Date(max)),
  };
}

function buildPortfolioFlowSeries(series, rangeQuery, bucketDays) {
  const src = Array.isArray(series) ? series : [];
  const { start, end } = resolvePortfolioSeriesRange(rangeQuery, src);
  if (!start || !end || !bucketDays) return [];
  const startDate = portfolioDateFromYmd(start);
  const endDate = portfolioDateFromYmd(end);
  if (!startDate || !endDate || startDate > endDate) return [];

  const totalDays =
    Math.floor((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
  const bucketCount = Math.ceil(totalDays / bucketDays);
  const buckets = Array.from({ length: bucketCount }, (_, i) => {
    const bucketStartDate = new Date(
      startDate.getTime() + i * bucketDays * 86400000
    );
    const bucketEndDate = new Date(
      Math.min(
        endDate.getTime(),
        bucketStartDate.getTime() + (bucketDays - 1) * 86400000
      )
    );
    const bucketStart = portfolioYmdIST(bucketStartDate);
    const bucketEnd = portfolioYmdIST(bucketEndDate);
    return { day: bucketStart, bucketStart, bucketEnd, cashIn: 0, cashOut: 0 };
  });

  const startMs = startDate.getTime();
  src.forEach((row) => {
    const day = row?.day || row?.date;
    const d = portfolioDateFromYmd(day);
    if (!d || d < startDate || d > endDate) return;
    const diffDays = Math.floor((d.getTime() - startMs) / 86400000);
    const bucketIndex = Math.floor(diffDays / bucketDays);
    const bucket = buckets[bucketIndex];
    if (!bucket) return;
    bucket.cashIn += Math.abs(Number(row.cashIn) || 0);
    bucket.cashOut += Math.abs(Number(row.cashOut) || 0);
  });

  return buckets;
}

/** Drops each series point’s cash-in / cash-out side independently when above `maxInr` (mirrored chart expects cashOut negative). */
function applyPortfolioFlowExcludeAboveSeries(series, maxInr) {
  const cap = Number(maxInr);
  if (!(cap > 0) || !Array.isArray(series)) return series;
  return series.map((row) => {
    const ciRaw = Number(row.cashIn) || 0;
    const coRaw = Number(row.cashOut) || 0;
    const ciAbs = Math.abs(ciRaw);
    const coAbs = Math.abs(coRaw);
    return {
      ...row,
      cashIn: ciAbs > cap ? 0 : ciRaw,
      cashOut: coAbs > cap ? 0 : coRaw,
    };
  });
}

const PortfolioFlowTooltip = ({ active, payload, label, onShowInfo }) => {
  if (!active || !payload || payload.length === 0) return null;
  const rows = payload.filter((p) => p && p.dataKey);
  if (!rows.length) return null;
  const datum = rows[0]?.payload;
  const rangeLabel =
    datum?.bucketStart && datum?.bucketEnd
      ? datum.bucketStart === datum.bucketEnd
        ? datum.bucketStart
        : `${datum.bucketStart} → ${datum.bucketEnd}`
      : label;
  return (
    <Box
      bg="white"
      color="gray.800"
      px={3}
      py={2}
      borderRadius="md"
      boxShadow="md"
      borderWidth="1px"
      borderColor="gray.200"
      minW="160px"
    >
      <Text fontSize="xs" fontWeight="semibold" mb={1}>
        {rangeLabel}
      </Text>
      {rows.map((r) => {
        const raw = r.value ?? 0;
        const val = Math.abs(Number(raw) || 0);
        return (
          <Flex key={r.dataKey} justify="space-between" fontSize="xs" gap={3}>
            <Text color="gray.600">{r.name}</Text>
            <Text fontWeight="semibold">{formatInr(val)}</Text>
          </Flex>
        );
      })}
      {datum?.bucketStart ? (
        <Text fontSize="xs" color="gray.500" mt={1}>
          Press I for cash in or O for cash out.
        </Text>
      ) : null}
      {onShowInfo && datum?.bucketStart && datum?.bucketEnd ? (
        <HStack mt={2} spacing={2} flexWrap="wrap">
          <Button
            size="xs"
            variant="outline"
            colorScheme="green"
            onClick={(event) => {
              event.stopPropagation();
              onShowInfo("cashIn", datum);
            }}
          >
            Show cash in
          </Button>
          <Button
            size="xs"
            variant="outline"
            colorScheme="red"
            onClick={(event) => {
              event.stopPropagation();
              onShowInfo("cashOut", datum);
            }}
          >
            Show cash out
          </Button>
        </HStack>
      ) : null}
    </Box>
  );
};

const PortfolioBarsTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <Box
      bg="white"
      color="gray.800"
      px={3}
      py={2}
      borderRadius="md"
      boxShadow="md"
      borderWidth="1px"
      borderColor="gray.200"
      minW="160px"
    >
      <Text fontSize="xs" fontWeight="semibold" mb={1}>
        {label}
      </Text>
      {payload.map((r) => (
        <Flex key={r.dataKey} justify="space-between" fontSize="xs" gap={3}>
          <Text color="gray.600">{r.name}</Text>
          <Text fontWeight="semibold">{formatInr(r.value)}</Text>
        </Flex>
      ))}
    </Box>
  );
};

/**
 * ResponsiveContainer often measures 0×0 inside Chakra fullscreen modals. This observes the
 * host box and renders Recharts with explicit width/height in pixels so SVGs lay out reliably.
 */
function PortfolioModalChartMeasure({ active, children, fallback }) {
  const wrapRef = useRef(null);
  const [dim, setDim] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    if (!active) {
      setDim({ w: 0, h: 0 });
      return undefined;
    }
    const el = wrapRef.current;
    if (!el) return undefined;

    const measure = () => {
      const r = el.getBoundingClientRect();
      const w = Math.max(2, Math.floor(r.width));
      const h = Math.max(2, Math.floor(r.height));
      setDim((p) => (p.w !== w || p.h !== h ? { w, h } : p));
    };

    measure();

    let raf = 0;
    const ro = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(measure)
      : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", measure);
    raf = requestAnimationFrame(measure);
    const tA = window.setTimeout(measure, 0);
    const tB = window.setTimeout(measure, 200);
    const tFallback = window.setTimeout(() => {
      const r = el.getBoundingClientRect();
      if (Math.floor(r.width) >= 50 && Math.floor(r.height) >= 50) return;
      if (typeof window === "undefined") return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setDim({
        w: Math.max(240, Math.floor(vw - 48)),
        h: Math.max(240, Math.floor(vh * 0.38)),
      });
    }, 400);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
      cancelAnimationFrame(raf);
      clearTimeout(tA);
      clearTimeout(tB);
      clearTimeout(tFallback);
    };
  }, [active]);

  return (
    <Box ref={wrapRef} w="100%" h="100%" minW={0} minH={0} position="relative">
      {!active || dim.w < 2 || dim.h < 2 ? (
        fallback ?? (
          <Flex w="full" h="full" align="center" justify="center">
            <Spinner color="teal.500" size="md" />
          </Flex>
        )
      ) : (
        children({ w: dim.w, h: dim.h })
      )}
    </Box>
  );
}

function parsePositiveAmount(value) {
  const n = parseFloat(String(value ?? "").replace(/,/g, ""));
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
}

/** sourceDate + sourceTime match Cashbook-style display in the transactions table. */
function newTransactionSourceDateTime() {
  const now = new Date();
  return {
    sourceDate: format(now, "dd MMMM yyyy"),
    sourceTime: format(now, "h:mm a"),
  };
}

/** YYYY-MM-DD for `<input type="date" />` using the device local calendar. */
function localDateInputValue(d) {
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatPortfolioSearchDateDisplay(tx) {
  const fromSource = [tx.sourceDate, tx.sourceTime].filter(Boolean).join(" ");
  if (fromSource) return fromSource;
  if (tx.date) {
    try {
      return format(new Date(tx.date), "dd MMMM yyyy h:mm a");
    } catch {
      return "—";
    }
  }
  return "—";
}

/** Descending order for search results (same priority as server: sourceDate → date → createdAt → _id). */
function portfolioSearchChronoMs(tx) {
  if (tx?.sourceDate) {
    try {
      const d = parse(String(tx.sourceDate).trim(), "dd MMMM yyyy", new Date());
      if (!Number.isNaN(d.getTime())) return d.getTime();
    } catch {
      /* ignore */
    }
  }
  if (tx?.date != null) {
    const d = new Date(tx.date);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  if (tx?.createdAt != null) {
    const d = new Date(tx.createdAt);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  const id = tx?._id;
  if (
    id != null &&
    String(id).length === 24 &&
    /^[a-f0-9]+$/i.test(String(id))
  ) {
    return parseInt(String(id).slice(0, 8), 16) * 1000;
  }
  return 0;
}

function sortPortfolioSearchRowsDesc(items) {
  return [...items].sort((a, b) => {
    const diff = portfolioSearchChronoMs(b) - portfolioSearchChronoMs(a);
    if (diff !== 0) return diff;
    return String(b._id ?? "").localeCompare(String(a._id ?? ""));
  });
}

/** Move one top-level folder before another; returns new array with portfolioRootOrder indices. */
function reorderPortfolioRootFolders(items, sourceId, targetId) {
  const fromIdx = items.findIndex(
    (c) => String(c._id) === String(sourceId)
  );
  const toIdx = items.findIndex(
    (c) => String(c._id) === String(targetId)
  );
  if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return null;
  const next = [...items];
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next.map((c, i) => ({ ...c, portfolioRootOrder: i }));
}

function cashFieldsForPortfolioAmount(amountNum, flow /* "in" | "out" */) {
  if (flow === "in") {
    return { cashIn: amountNum, cashOut: 0, isDebit: false };
  }
  return { cashIn: 0, cashOut: amountNum, isDebit: true };
}

function cashFieldsForPortfolioUpdate(existing, amountNum) {
  if (!existing) return {};
  const ci = Number(existing.cashIn ?? 0);
  const co = Number(existing.cashOut ?? 0);
  if (co !== 0 && ci === 0) {
    return { cashIn: 0, cashOut: amountNum, isDebit: true };
  }
  if (ci !== 0 && co === 0) {
    return { cashIn: amountNum, cashOut: 0, isDebit: false };
  }
  if (existing.isDebit === true) {
    return { cashIn: 0, cashOut: amountNum, isDebit: true };
  }
  return { cashIn: amountNum, cashOut: 0, isDebit: false };
}

/**
 * API / Mongo may send booleans as strings; strict `=== false` misses `"false"`.
 * Returns true = debit, false = credit, null = unknown.
 */
function parsePortfolioDebitFlag(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no") return false;
  }
  return null;
}

/** Normalize legacy array or paginated `{ items, total, ... }` from GET /portfolios/transactions. */
function parsePortfolioTransactionsResponse(apiResult) {
  const raw = apiResult?.data;
  if (Array.isArray(raw)) {
    return {
      items: raw,
      total: raw.length,
      page: 1,
      limit: raw.length || 50,
      totalPages: 1,
    };
  }
  return {
    items: raw?.items ?? [],
    total: raw?.total ?? 0,
    page: raw?.page ?? 1,
    limit: raw?.limit ?? 50,
    totalPages: raw?.totalPages ?? 1,
  };
}

/** Max supporting files per transaction (must match server cap). */
const MAX_PORTFOLIO_SUPPORTING_DOC_FILES = 12;

function normalizePortfolioTransactionFileUrls(raw) {
  if (!raw || !Array.isArray(raw)) return [];
  return raw
    .filter((u) => typeof u === "string" && /^https?:\/\//i.test(u.trim()))
    .map((u) => u.trim());
}

function portfolioSupportingDocumentDisplayName(url, index) {
  try {
    const path = new URL(url).pathname.split("/").filter(Boolean).pop();
    if (path) return decodeURIComponent(path);
  } catch {
    // ignore
  }
  return `Document ${index + 1}`;
}

async function uploadPortfolioSupportingFileToAws(file) {
  const formData = new FormData();
  formData.append("file", file);
  const token = getCookieToken();
  const response = await axios.post(`${serverBaseUrl}/upload/aws`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      authorization: `Bearer ${token}`,
    },
  });
  if (!response.data?.success || !response.data?.data?.fileUrl) {
    throw new Error(
      typeof response.data?.message === "string"
        ? response.data.message
        : "File upload failed."
    );
  }
  return response.data.data.fileUrl;
}

const PORTFOLIO_TX_PAGE_SIZE = 50;

function isLikelyMongoObjectId(s) {
  return typeof s === "string" && /^[a-f\d]{24}$/i.test(s.trim());
}

/** Pretty INR display for category totals (Indian grouping). */
function formatINRPortfolioValue(raw) {
  if (raw === null || raw === undefined || raw === "") return "0";
  const n = Number(raw);
  if (Number.isNaN(n)) return String(raw);
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Row background: red = debit, green = credit — driven only by `isDebit`. */
function getPortfolioCashFlowTone(t) {
  const debit = parsePortfolioDebitFlag(t?.isDebit ?? t?.is_debit);
  if (debit === true) return "out";
  if (debit === false) return "in";
  return null;
}

/** Card-style folder row for root categories (teal) or nested subcategories (blue). */
function PortfolioCategoryFolderCard({
  category,
  variant = "teal",
  isEditingThisRow,
  editedCategoryName,
  onEditedCategoryNameChange,
  onSelect,
  onSaveEdit,
  onStartEdit,
  onDelete,
  onCombinedBranch,
  combinedBranchAriaLabel = "Show every transaction in this folder and nested folders",
  deleteAriaLabel,
  saveAriaLabel = "Save name",
  editAriaLabel = "Edit name",
  borderColor,
  textColor,
  inputBgColor,
  amountColor,
  categoryRowBg,
  categoryRowHoverBg,
  categoryIconBg,
  onToggleHideAmountSum,
  hideAmountSumSaving = false,
  /** Optional drag handle (e.g. top-level portfolio home grid) */
  dragHandleSlot = null,
  collaborationShared = false,
  collaborationCanInvite = false,
  onShareClick,
}) {
  const iconPlateBg = useColorModeValue("white", "gray.700");
  const hideSum = !!category.portfolioHideAmountSum;
  const isBlue = variant === "blue";
  const borderAccent = isBlue ? "blue.400" : "teal.400";
  const hoverBorder = isBlue ? "blue.300" : "teal.300";
  const folderColor = isBlue ? "blue.500" : "teal.500";
  const chevronHover = folderColor;
  const amountShellBg = useColorModeValue(
    isBlue
      ? "linear-gradient(145deg, #eff6ff 0%, #dbeafe 55%, #e0e7ff 100%)"
      : "linear-gradient(145deg, #f0fdfa 0%, #ccfbf1 55%, #d1fae5 100%)",
    isBlue
      ? "linear-gradient(145deg, rgba(30, 64, 175, 0.35) 0%, rgba(37, 99, 235, 0.2) 100%)"
      : "linear-gradient(145deg, rgba(15, 118, 110, 0.4) 0%, rgba(13, 148, 136, 0.22) 100%)"
  );
  const amountShellBorder = useColorModeValue(
    isBlue ? "blue.200" : "teal.200",
    isBlue ? "blue.500" : "teal.500"
  );
  const rupeeMuted = useColorModeValue("gray.500", "gray.400");
  const negativeNetRupeeColor = useColorModeValue("red.500", "red.400");
  const negativeNetAmountColor = useColorModeValue("red.600", "red.300");
  const amountShellBgNegative = useColorModeValue(
    "linear-gradient(145deg, #fff5f5 0%, #fee2e2 50%, #fecdd3 100%)",
    "linear-gradient(145deg, rgba(127, 29, 29, 0.3) 0%, rgba(185, 28, 28, 0.14) 100%)"
  );
  const amountShellBorderNegative = useColorModeValue("red.200", "red.500");
  const negativeAmountTopAccent =
    "linear(to-r, red.400, rose.400)";
  const cardSectionDivider = useColorModeValue("gray.200", "whiteAlpha.200");
  const footerMutedBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50");
  const showHideToggle = onToggleHideAmountSum && !isEditingThisRow;

  const netParsed = Number(category?.totalSum);
  const hasNet =
    !hideSum &&
    category?.totalSum !== undefined &&
    category?.totalSum !== null &&
    category?.totalSum !== "";
  const isNegativeNet =
    hasNet && !Number.isNaN(netParsed) && netParsed < 0;
  const resolvedAmountShellBg = isNegativeNet ? amountShellBgNegative : amountShellBg;
  const resolvedAmountShellBorder = isNegativeNet
    ? amountShellBorderNegative
    : amountShellBorder;
  const resolvedTopAccentGradient = isNegativeNet
    ? negativeAmountTopAccent
    : isBlue
      ? "linear(to-r, blue.400, indigo.400)"
      : "linear(to-r, teal.400, emerald.400)";
  const resolvedRupeeColor = isNegativeNet ? negativeNetRupeeColor : rupeeMuted;
  const resolvedAmountColor = isNegativeNet ? negativeNetAmountColor : amountColor;

  return (
    <Box
      role="group"
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      borderLeftWidth="4px"
      borderLeftColor={borderAccent}
      bg={categoryRowBg}
      cursor="pointer"
      overflow="hidden"
      boxShadow="sm"
      transition="all 0.2s ease"
      onClick={onSelect}
      _hover={{
        bg: categoryRowHoverBg,
        boxShadow: "md",
        borderColor: hoverBorder,
        transform: "translateY(-2px)",
      }}
    >
      <Flex
        align="center"
        gap={3}
        px={{ base: 3, md: 4 }}
        py={3}
        bg={categoryIconBg}
        borderBottomWidth="1px"
        borderColor={borderColor}
      >
        {dragHandleSlot ? (
          <Box flexShrink={0} onClick={(e) => e.stopPropagation()}>
            {dragHandleSlot}
          </Box>
        ) : null}
        <Flex
          align="center"
          justify="center"
          w="40px"
          h="40px"
          borderRadius="lg"
          bg={iconPlateBg}
          flexShrink={0}
          boxShadow="sm"
        >
          <Icon as={FiFolder} w={5} h={5} color={folderColor} />
        </Flex>
        <Box flex="1" minW={0} textAlign="left">
          {isEditingThisRow ? (
            <Input
              value={editedCategoryName}
              onChange={(e) => {
                e.stopPropagation();
                onEditedCategoryNameChange(e.target.value);
              }}
              size="sm"
              variant="outline"
              bg={inputBgColor}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <Flex align="center" gap={2} minW={0} wrap="wrap">
              <Text
                fontWeight="bold"
                fontSize={{ base: "sm", md: "md" }}
                noOfLines={{ base: 2, md: 2 }}
                color={textColor}
                title={category.name}
                lineHeight="short"
                flex="1"
                minW={0}
              >
                {category.name}
              </Text>
              {collaborationShared ? (
                <Badge colorScheme="purple" fontSize="10px" borderRadius="md" flexShrink={0}>
                  Shared with you
                </Badge>
              ) : null}
            </Flex>
          )}
        </Box>
      </Flex>
      <VStack align="stretch" spacing={0} w="100%">
        <Box px={{ base: 3, md: 4 }} pt={3} pb={3}>
          <Box
            w="100%"
            px={{ base: 3, md: 3.5 }}
            py={{ base: 2.5, md: 3 }}
            borderRadius="xl"
            bg={resolvedAmountShellBg}
            borderWidth="1px"
            borderColor={resolvedAmountShellBorder}
            boxShadow="sm"
            position="relative"
            overflow="hidden"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              h: "3px",
              bgGradient: resolvedTopAccentGradient,
              opacity: 0.85,
            }}
          >
            <Text
              fontSize="10px"
              fontWeight="semibold"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.08em"
              mb={1.5}
              _dark={{ color: "gray.400" }}
            >
              Total balance
            </Text>
            {hideSum ? (
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                fontWeight="semibold"
                color="gray.500"
                fontStyle="italic"
                letterSpacing="0.02em"
              >
                Hidden
              </Text>
            ) : (
              <HStack spacing={1.5} align="baseline" flexWrap="wrap">
                <Text
                  as="span"
                  fontSize="sm"
                  fontWeight="bold"
                  color={resolvedRupeeColor}
                  fontVariantNumeric="tabular-nums"
                >
                  ₹
                </Text>
                <Text
                  as="span"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="extrabold"
                  fontVariantNumeric="tabular-nums"
                  letterSpacing="-0.03em"
                  lineHeight="1.1"
                  color={resolvedAmountColor}
                  title={formatINRPortfolioValue(category.totalSum)}
                >
                  {formatINRPortfolioValue(category.totalSum)}
                </Text>
              </HStack>
            )}
          </Box>
        </Box>

        <Flex
          role="presentation"
          align={{ base: "stretch", sm: "center" }}
          justify={showHideToggle ? "space-between" : "flex-end"}
          flexWrap="wrap"
          gap={{ base: 2, sm: 3 }}
          px={{ base: 3, md: 4 }}
          py={2.5}
          borderTopWidth="1px"
          borderTopColor={cardSectionDivider}
          bg={footerMutedBg}
        >
          {showHideToggle ? (
            <Box flex={{ base: "1 1 100%", sm: "1 1 auto" }} minW={0}>
              <Tooltip
                label={
                  hideSum
                    ? "Show total balance on this card"
                    : "Hide total balance on this card (saved for this folder)"
                }
                hasArrow
                openDelay={300}
              >
                <FormControl
                  display="flex"
                  alignItems="center"
                  gap={2}
                  w="fit-content"
                  maxW="100%"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FormLabel
                    htmlFor={`phs-${String(category._id)}`}
                    mb={0}
                    fontSize="xs"
                    fontWeight="medium"
                    color="gray.600"
                    cursor="pointer"
                    _dark={{ color: "gray.400" }}
                  >
                    Hide total
                  </FormLabel>
                  <Switch
                    id={`phs-${String(category._id)}`}
                    size="sm"
                    colorScheme="teal"
                    isChecked={hideSum}
                    isDisabled={hideAmountSumSaving}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleHideAmountSum(e.target.checked);
                    }}
                  />
                </FormControl>
              </Tooltip>
            </Box>
          ) : null}

          <HStack
            spacing={0.5}
            flexWrap="wrap"
            justify="flex-end"
            flexShrink={0}
            onClick={(e) => e.stopPropagation()}
          >
            {onShareClick &&
            !isEditingThisRow &&
            (!collaborationShared || collaborationCanInvite) ? (
              <Tooltip
                label="Invite others — same portfolio folder for everyone"
                hasArrow
                openDelay={300}
              >
                <IconButton
                  icon={<Icon as={FiShare2} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="purple"
                  aria-label="Share portfolio folder"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShareClick();
                  }}
                />
              </Tooltip>
            ) : null}
            {onCombinedBranch && !isEditingThisRow && (
              <Tooltip label={combinedBranchAriaLabel} hasArrow openDelay={300}>
                <IconButton
                  icon={<Icon as={FiList} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="purple"
                  aria-label={combinedBranchAriaLabel}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCombinedBranch();
                  }}
                />
              </Tooltip>
            )}
            {isEditingThisRow ? (
              <IconButton
                icon={<CheckIcon />}
                size="sm"
                variant="ghost"
                aria-label={saveAriaLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  onSaveEdit();
                }}
              />
            ) : (
              <IconButton
                icon={<EditIcon />}
                size="sm"
                variant="ghost"
                aria-label={editAriaLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  onStartEdit();
                }}
              />
            )}
            <DeleteButtonWithConfirmation
              onDelete={onDelete}
              itemId={category._id}
              size="sm"
              colorScheme="red"
              ariaLabel={deleteAriaLabel}
            />
            <Icon
              as={FiChevronRight}
              display={{ base: "none", sm: "block" }}
              w={5}
              h={5}
              color="gray.400"
              mx={1}
              _groupHover={{ color: chevronHover }}
              aria-hidden
            />
          </HStack>
        </Flex>
      </VStack>
    </Box>
  );
}

const PortfolioManager = () => {
  const toast = useToast();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [hideAmountSavingId, setHideAmountSavingId] = useState(null);
  const [draggingPortfolioRootId, setDraggingPortfolioRootId] = useState(null);
  const [portfolioRootOrderSaving, setPortfolioRootOrderSaving] =
    useState(false);
  const sharePortfolioModal = useDisclosure();
  const [sharePortfolioCategory, setSharePortfolioCategory] = useState(null);
  const [subTransactions, setSubTransactions] = useState([]);
  /** Merge all transactions in a category subtree, sorted by date; hides subfolder grid when on. */
  const [portfolioCombinedMode, setPortfolioCombinedMode] = useState(false);
  const [combinedLedgerRootCategory, setCombinedLedgerRootCategory] =
    useState(null);
  const [combinedLedgerTransactions, setCombinedLedgerTransactions] =
    useState([]);
  const [combinedCategoryNameById, setCombinedCategoryNameById] = useState(
    {}
  );
  const [combinedLedgerLoading, setCombinedLedgerLoading] = useState(false);
  const [combinedLedgerPage, setCombinedLedgerPage] = useState(1);
  const [combinedLedgerTotalPages, setCombinedLedgerTotalPages] = useState(1);
  const [combinedLedgerTotal, setCombinedLedgerTotal] = useState(0);
  /** Immediate children of combined ledger root (for add/edit transaction folder picker in merged view). */
  const [combinedModeChildCategories, setCombinedModeChildCategories] =
    useState([]);
  /** Sibling folders under the same parent (for moving transactions when editing). */
  const [portfolioSiblingCategories, setPortfolioSiblingCategories] =
    useState([]);

  const [subTransactionsPage, setSubTransactionsPage] = useState(1);
  const [subTransactionsTotalPages, setSubTransactionsTotalPages] =
    useState(1);
  const [subTransactionsTotal, setSubTransactionsTotal] = useState(0);

  const [transactionName, setTransactionName] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [transactionAmount, setTransactionAmount] = useState("");
  /** UI-created rows: cash in (green) vs cash out (red) */
  const [newTransactionCashFlow, setNewTransactionCashFlow] = useState("out");
  /** When inside a category: post to this category or an immediate child */
  const [transactionCategoryId, setTransactionCategoryId] = useState("");
  const [portfolioVoiceDraft, setPortfolioVoiceDraft] = useState("");
  const [portfolioVoiceReply, setPortfolioVoiceReply] = useState("");
  const [portfolioVoiceLoading, setPortfolioVoiceLoading] = useState(false);
  const [portfolioVoiceListening, setPortfolioVoiceListening] = useState(false);
  const [portfolioVoiceSpeaking, setPortfolioVoiceSpeaking] = useState(false);
  const [portfolioVoiceCacheClearing, setPortfolioVoiceCacheClearing] =
    useState(false);
  /** Server-driven follow-up: user must pick a subfolder by voice/text. */
  const [, setPortfolioVoicePending] = useState(null);
  const [portfolioVoiceSuggested, setPortfolioVoiceSuggested] = useState([]);
  const portfolioVoicePendingRef = useRef(null);
  const portfolioVoiceDictationRef = useRef(null);
  const portfolioVoiceSkipAutoSendRef = useRef(false);
  const submitPortfolioVoiceAssistantRef = useRef(async () => {});
  const [portfolioSttLang, setPortfolioSttLang] = useState(() =>
    getPreferredSttLang()
  );
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(null); // To track which category  is being edited
  const [editedCategoryName, setEditedCategoryName] = useState("");
  const [editedTransactionDesc, setEditedTransactionDesc] = useState("");
  const [editedTransactionAmount, setEditedTransactionAmount] = useState("");
  /** Target portfolio folder when editing a transaction (siblings, this folder, or subfolders). */
  const [editedTransactionCategoryId, setEditedTransactionCategoryId] =
    useState("");
  const [categoryPath, setCategoryPath] = useState([]);

  const portfolioSpeechCategoryNames = useMemo(() => {
    const names = new Set();
    for (const c of categories) {
      if (c?.name) names.add(String(c.name));
    }
    for (const c of subCategories) {
      if (c?.name) names.add(String(c.name));
    }
    for (const c of categoryPath) {
      if (c?.name) names.add(String(c.name));
    }
    if (selectedCategory?.name) names.add(String(selectedCategory.name));
    return [...names];
  }, [categories, subCategories, categoryPath, selectedCategory?.name]);

  // Define color mode values
  const boxBgColor = useColorModeValue("white", "gray.700");
  const headingColor = useColorModeValue("blue.600", "blue.300");
  const inputBgColor = useColorModeValue("gray.50", "gray.800");
  const focusBorderColor = useColorModeValue("blue.500", "blue.300");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "gray.200");
  const amountColor = useColorModeValue("green.600", "green.400");
  const folderHeaderNegativeTotalColor = useColorModeValue("red.600", "red.300");
  // Define colors for light and dark mode
  const inputBorderColor = useColorModeValue("teal.500", "teal.300"); // Border color on focus for input
  const buttonColorScheme = useColorModeValue("teal", "teal"); // Color scheme for button (same in both modes)
  const breadcrumbBg = useColorModeValue("gray.100", "gray.700");
  const breadcrumbBorder = useColorModeValue("gray.200", "gray.600");
  const breadcrumbCurrentBg = useColorModeValue("teal.100", "teal.800");
  const breadcrumbCurrentColor = useColorModeValue("teal.700", "teal.100");
  const breadcrumbLinkColor = useColorModeValue("gray.700", "gray.200");
  const breadcrumbLinkHoverBg = useColorModeValue("white", "gray.600");
  const tableHeaderBg = useColorModeValue("gray.100", "gray.600");
  const neutralTableRowBg = useColorModeValue("white", "gray.800");
  const portfolioRowNeutralHoverBg = useColorModeValue("gray.100", "gray.700");
  const tableRowHoverOut = useColorModeValue("red.100", "rgba(220, 38, 38, 0.22)");
  const tableRowHoverIn = useColorModeValue(
    "green.100",
    "rgba(22, 163, 74, 0.22)"
  );
  const categoryRowBg = useColorModeValue("white", "gray.800");
  const categoryRowHoverBg = useColorModeValue("gray.50", "gray.700");
  const categoryIconBgRoot = useColorModeValue("teal.50", "teal.900");
  const categoryIconBgNested = useColorModeValue("blue.50", "blue.900");
  const catOptionHoverBg = useColorModeValue("gray.100", "gray.700");
  const catOptionSelectedBg = useColorModeValue("blue.50", "blue.900");
  const loadingOverlayBg = useColorModeValue(
    "rgba(255, 255, 255, 0.85)",
    "rgba(26, 32, 44, 0.85)"
  );
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const mutedLine = useColorModeValue("gray.100", "gray.700");
  const surfaceCard = useColorModeValue("white", "gray.800");
  const assistantHeaderBg = useColorModeValue("gray.50", "gray.900");
  const locationStripBg = useColorModeValue("blue.50", "rgba(59, 130, 246, 0.12)");
  const subtleLabel = useColorModeValue("gray.600", "gray.400");
  const categorySectionBodyBg = useColorModeValue("gray.50", "gray.900");
  const chartAxisColor = useColorModeValue("#64748b", "#94a3b8");
  const chartGridColor = useColorModeValue("#e2e8f033", "#ffffff14");
  const portfolioCashOutRowBg = useColorModeValue(
    "red.50",
    "rgba(220, 38, 38, 0.12)"
  );
  const portfolioCashInRowBg = useColorModeValue(
    "green.50",
    "rgba(22, 163, 74, 0.12)"
  );
  const [isCategoryBeingSelected, setIsCategoryBeingSelected] = useState(false);
  const [isPortfolioInitializing, setIsPortfolioInitializing] = useState(true);
  /** Synchronous guard — state updates too late to block double-clicks before re-render */
  const categoryNavInFlightRef = useRef(false);
  const [pendingForceDeleteCategoryId, setPendingForceDeleteCategoryId] =
    useState(null);
  const [importParentCategoryName, setImportParentCategoryName] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [isImportingCsv, setIsImportingCsv] = useState(false);
  const cancelForceDeleteRef = useRef();
  const {
    isOpen: isForceDeleteDialogOpen,
    onOpen: openForceDeleteDialog,
    onClose: closeForceDeleteDialog,
  } = useDisclosure();
  const {
    isOpen: isImportModalOpen,
    onOpen: openImportModal,
    onClose: closeImportModal,
  } = useDisclosure();
  const [analyticsSubtreeCategories, setAnalyticsSubtreeCategories] = useState(
    []
  );
  const [analyticsSelectedIds, setAnalyticsSelectedIds] = useState([]);
  const [analyticsMode, setAnalyticsMode] = useState("summary");
  /** Optional YYYY-MM-DD; empty = no bound (all time when both empty). */
  const [reportDateFrom, setReportDateFrom] = useState("");
  const [reportDateTo, setReportDateTo] = useState("");
  const [analyticsLoadingSubtree, setAnalyticsLoadingSubtree] =
    useState(false);
  const [analyticsPdfBusy, setAnalyticsPdfBusy] = useState(false);
  /** At most one tool open: transaction | subfolder | pdf | null */
  const [activeFolderTool, setActiveFolderTool] = useState(null);
  const [savedPortfolioReports, setSavedPortfolioReports] = useState([]);
  const [pendingPdfSave, setPendingPdfSave] = useState(null);
  const cancelSaveReportRef = useRef();
  const {
    isOpen: isSaveReportDialogOpen,
    onOpen: onOpenSaveReportDialog,
    onClose: onCloseSaveReportDialog,
  } = useDisclosure();
  const transactionCategoryPicker = useDisclosure();
  const transactionAttachmentsModal = useDisclosure();
  const [transactionAttachmentsModalUrls, setTransactionAttachmentsModalUrls] =
    useState([]);
  const [transactionAttachmentsModalTitle, setTransactionAttachmentsModalTitle] =
    useState("");
  const [pendingTransactionSupportingFiles, setPendingTransactionSupportingFiles] =
    useState([]);
  const [transactionSupportingDocsBusy, setTransactionSupportingDocsBusy] =
    useState(false);
  const supportingTransactionDocsInputRef = useRef(null);
  const [isFlowCategoryBalanceOpen, setIsFlowCategoryBalanceOpen] =
    useState(false);
  const [transactionCategorySearch, setTransactionCategorySearch] =
    useState("");
  const transactionCategorySearchRef = useRef(null);

  const [portfolioSearchQuery, setPortfolioSearchQuery] = useState("");
  const [portfolioSearchPage, setPortfolioSearchPage] = useState(1);
  const [portfolioSearchRows, setPortfolioSearchRows] = useState([]);
  const [portfolioSearchTotal, setPortfolioSearchTotal] = useState(0);
  const [portfolioSearchTotalPages, setPortfolioSearchTotalPages] =
    useState(1);
  const [portfolioSearchLoading, setPortfolioSearchLoading] = useState(false);
  const [portfolioSearchScopeLabel, setPortfolioSearchScopeLabel] =
    useState("");
  const portfolioSearchDebounceRef = useRef(null);
  const [portfolioSearchMatchDescription, setPortfolioSearchMatchDescription] =
    useState(true);
  const [portfolioSearchMatchCategory, setPortfolioSearchMatchCategory] =
    useState(true);
  const [portfolioFolderVizBusy, setPortfolioFolderVizBusy] = useState(false);
  const [portfolioDailyViz, setPortfolioDailyViz] = useState(null);
  const [portfolioChildrenViz, setPortfolioChildrenViz] = useState(null);
  const [portfolioAnalyticsRange, setPortfolioAnalyticsRange] = useState("60d");
  const [portfolioAnalyticsCustomStart, setPortfolioAnalyticsCustomStart] =
    useState("");
  const [portfolioAnalyticsCustomEnd, setPortfolioAnalyticsCustomEnd] =
    useState("");
  const [flowBucketInfoOpen, setFlowBucketInfoOpen] = useState(false);
  const [flowBucketInfoLoading, setFlowBucketInfoLoading] = useState(false);
  const [flowBucketInfoRows, setFlowBucketInfoRows] = useState([]);
  const [flowBucketInfoRange, setFlowBucketInfoRange] = useState({
    start: "",
    end: "",
  });
  const [flowBucketInfoMode, setFlowBucketInfoMode] = useState("cashIn");
  const [flowBucketInfoTruncated, setFlowBucketInfoTruncated] = useState(false);
  const [flowHoverDatum, setFlowHoverDatum] = useState(null);
  const [portfolioFlowExcludeAboveInput, setPortfolioFlowExcludeAboveInput] =
    useState("");

  const portfolioFlowExcludeAboveParsedInr = useMemo(() => {
    const n = Number(
      String(portfolioFlowExcludeAboveInput ?? "").replace(/,/g, "").trim()
    );
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [portfolioFlowExcludeAboveInput]);

  const portfolioFlowModalHasBars =
    (portfolioChildrenViz?.children?.length ?? 0) > 0;

  const portfolioAnalyticsRangeLabel = useMemo(() => {
    const match = PORTFOLIO_ANALYTICS_RANGES.find(
      (r) => r.id === portfolioAnalyticsRange
    );
    return match ? match.label : "60 days";
  }, [portfolioAnalyticsRange]);

  const portfolioAnalyticsRangeQuery = useMemo(() => {
    const match = PORTFOLIO_ANALYTICS_RANGES.find(
      (r) => r.id === portfolioAnalyticsRange
    );
    if (!match || !match.days) {
      if (match?.id === "custom") {
        return {
          start: portfolioAnalyticsCustomStart,
          end: portfolioAnalyticsCustomEnd,
        };
      }
      return { start: "", end: "" };
    }
    return portfolioDefaultRangeLastDaysIst(match.days);
  }, [portfolioAnalyticsRange, portfolioAnalyticsCustomStart, portfolioAnalyticsCustomEnd]);

  const portfolioFlowBucketDays = useMemo(
    () => portfolioAnalyticsBucketDays(portfolioAnalyticsRange, portfolioAnalyticsRangeQuery),
    [portfolioAnalyticsRange, portfolioAnalyticsRangeQuery]
  );

  const portfolioFlowModalSeries = useMemo(() => {
    const src = portfolioDailyViz?.series;
    const normalized = buildPortfolioFlowSeries(
      src,
      portfolioAnalyticsRangeQuery,
      portfolioFlowBucketDays
    );
    return normalized.map((row) => ({
      ...row,
      cashIn: Math.abs(Number(row.cashIn) || 0),
      cashOut: -Math.abs(Number(row.cashOut) || 0),
    }));
  }, [portfolioDailyViz?.series, portfolioAnalyticsRangeQuery, portfolioFlowBucketDays]);

  const portfolioFlowModalSeriesForChart = useMemo(
    () =>
      applyPortfolioFlowExcludeAboveSeries(
        portfolioFlowModalSeries,
        portfolioFlowExcludeAboveParsedInr
      ),
    [portfolioFlowModalSeries, portfolioFlowExcludeAboveParsedInr]
  );

  const portfolioFlowChartAllExcludedByThreshold = useMemo(() => {
    if (!(portfolioFlowExcludeAboveParsedInr > 0) || !portfolioFlowModalSeries.length) {
      return false;
    }
    const rawMax = portfolioFlowModalSeries.reduce((max, row) => {
      const v = Math.max(
        Math.abs(Number(row.cashIn) || 0),
        Math.abs(Number(row.cashOut) || 0)
      );
      return Math.max(max, v);
    }, 0);
    if (!(rawMax > 0)) return false;
    const shownMax = portfolioFlowModalSeriesForChart.reduce((max, row) => {
      const v = Math.max(
        Math.abs(Number(row.cashIn) || 0),
        Math.abs(Number(row.cashOut) || 0)
      );
      return Math.max(max, v);
    }, 0);
    return !(shownMax > 0);
  }, [
    portfolioFlowExcludeAboveParsedInr,
    portfolioFlowModalSeries,
    portfolioFlowModalSeriesForChart,
  ]);

  const portfolioChildrenVizBarsData = useMemo(() => {
    const children = portfolioChildrenViz?.children ?? [];
    const cap = portfolioFlowExcludeAboveParsedInr;
    return children.map((c) => {
      let cashIn = Number(c.cashIn) || 0;
      let cashOut = Number(c.cashOut) || 0;
      if (cap > 0) {
        if (cashIn > cap) cashIn = 0;
        if (cashOut > cap) cashOut = 0;
      }
      return {
        label:
          String(c.name || "—").length > 26
            ? `${String(c.name).slice(0, 25)}…`
            : String(c.name || "—"),
        cashIn,
        cashOut,
        capped: !!c.capped,
      };
    });
  }, [portfolioChildrenViz?.children, portfolioFlowExcludeAboveParsedInr]);

  const portfolioFlowModalLoadingSeries =
    portfolioFolderVizBusy &&
    portfolioFlowModalSeries.length === 0;
  const portfolioFlowModalHasDailySeries = portfolioFlowModalSeries.length > 0;
  const portfolioFlowModalEmptyGlobally =
    !portfolioFolderVizBusy &&
    !(portfolioChildrenViz?.children?.length) &&
    !portfolioFlowModalSeries.length;

  const portfolioFlowAbsoluteMax = useMemo(() => {
    if (!portfolioFlowModalSeriesForChart.length) return 0;
    return portfolioFlowModalSeriesForChart.reduce((max, row) => {
      const v = Math.max(
        Math.abs(Number(row.cashIn) || 0),
        Math.abs(Number(row.cashOut) || 0)
      );
      return Math.max(max, v);
    }, 0);
  }, [portfolioFlowModalSeriesForChart]);

  const portfolioFlowAxisMax = useMemo(() => {
    if (portfolioFlowAbsoluteMax <= 0) return 1;
    return Math.ceil(portfolioFlowAbsoluteMax * 1.08);
  }, [portfolioFlowAbsoluteMax]);

  const portfolioFlowChartHeight = useMemo(
    () => chartHeightForPoints(portfolioFlowModalSeries.length, 560, 1400),
    [portfolioFlowModalSeries.length]
  );
  const portfolioBarsChartHeight = useMemo(() => {
    const rows = portfolioChildrenViz?.children?.length || 0;
    return Math.max(420, 200 + rows * 26);
  }, [portfolioChildrenViz?.children?.length]);

  const runPortfolioSearch = useCallback(
    async (page) => {
      const q = portfolioSearchQuery.trim();
      if (!q) {
        return;
      }
      if (!portfolioSearchMatchDescription && !portfolioSearchMatchCategory) {
        toast({
          title: "Select where to search",
          description:
            "Turn on at least one: Description and/or Folder name (category).",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      setPortfolioSearchLoading(true);
      try {
        const params = new URLSearchParams({
          q,
          page: String(page),
          limit: String(PORTFOLIO_TX_PAGE_SIZE),
        });
        params.set(
          "searchDescription",
          portfolioSearchMatchDescription ? "true" : "false"
        );
        params.set(
          "searchCategory",
          portfolioSearchMatchCategory ? "true" : "false"
        );
        if (selectedCategory?._id) {
          params.set("rootCategoryId", selectedCategory._id);
        }
        params.set("_cb", String(Date.now()));
        const res = await hitBeApi(
          `portfolios/transactions/search?${params.toString()}`,
          "GET"
        );
        if (!res?.success) {
          throw new Error(res?.message || "Search failed.");
        }
        const p = res.data;
        const rawItems = Array.isArray(p.items) ? p.items : [];
        setPortfolioSearchRows(sortPortfolioSearchRowsDesc(rawItems));
        setPortfolioSearchTotal(typeof p.total === "number" ? p.total : 0);
        setPortfolioSearchTotalPages(
          typeof p.totalPages === "number" ? p.totalPages : 1
        );
        setPortfolioSearchPage(typeof p.page === "number" ? p.page : page);
        setPortfolioSearchScopeLabel(p.scopeLabel || "");
      } catch (e) {
        toast({
          title: "Search failed",
          description: e?.message || "Try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setPortfolioSearchLoading(false);
      }
    },
    [
      portfolioSearchQuery,
      selectedCategory?._id,
      portfolioSearchMatchDescription,
      portfolioSearchMatchCategory,
      toast,
    ]
  );

  const portfolioLevelLabel = useMemo(() => {
    if (!selectedCategory) {
      return { level: 0, pathLabel: "Portfolio root — open a folder to drill in" };
    }
    const depth = categoryPath.length;
    const pathLabel =
      categoryPath.length > 0
        ? categoryPath.map((c) => c.name).join(" › ")
        : selectedCategory.name;
    return { level: depth, pathLabel };
  }, [selectedCategory, categoryPath]);

  useEffect(() => {
    const q = portfolioSearchQuery.trim();
    if (!portfolioSearchMatchDescription && !portfolioSearchMatchCategory) {
      setPortfolioSearchRows([]);
      setPortfolioSearchTotal(0);
      setPortfolioSearchTotalPages(1);
      setPortfolioSearchPage(1);
      setPortfolioSearchScopeLabel("");
      return;
    }
    if (!q) {
      setPortfolioSearchRows([]);
      setPortfolioSearchTotal(0);
      setPortfolioSearchTotalPages(1);
      setPortfolioSearchPage(1);
      setPortfolioSearchScopeLabel("");
      return;
    }
    if (portfolioSearchDebounceRef.current) {
      clearTimeout(portfolioSearchDebounceRef.current);
    }
    portfolioSearchDebounceRef.current = setTimeout(() => {
      runPortfolioSearch(1);
    }, 450);
    return () => {
      if (portfolioSearchDebounceRef.current) {
        clearTimeout(portfolioSearchDebounceRef.current);
      }
    };
  }, [
    portfolioSearchQuery,
    selectedCategory?._id,
    portfolioSearchMatchDescription,
    portfolioSearchMatchCategory,
    runPortfolioSearch,
  ]);

  const executeCategoryDelete = async (
    portfolioCategoryId,
    forceDeleteAssociatedItems = false
  ) => {
    const deletePortfolioCategoryResponse = await hitBeApi(
      `categories/${portfolioCategoryId}${
        forceDeleteAssociatedItems ? "?forceDeleteAssociatedItems=true" : ""
      }`,
      "DELETE"
    );

    if (deletePortfolioCategoryResponse.success) {
      if (selectedCategory) {
        const updatedSubCategories = subCategories.filter(
          (category) => category._id !== portfolioCategoryId
        );
        setSubCategories(updatedSubCategories);
      } else {
        const updatedPortfolioCategories = categories.filter(
          (category) => category._id !== portfolioCategoryId
        );
        setCategories(updatedPortfolioCategories);
      }
    }
    return deletePortfolioCategoryResponse;
  };

  const handleStartEdit = (item, type) => {
    setIsEditing({ id: item._id, type });
    if (type === "category") {
      setEditedCategoryName(item.name);
    } else {
      setEditedTransactionDesc(item.desc ?? "");
      setEditedTransactionAmount(
        item.amount != null ? String(item.amount) : ""
      );
      const cid = item.categoryId?._id ?? item.categoryId ?? selectedCategory?._id;
      setEditedTransactionCategoryId(cid != null ? String(cid) : "");
    }
  };


  // Parent view: load category summaries only (no global transaction dump).
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      flushSync(() => {
        setIsPortfolioInitializing(true);
      });
      // Let the browser paint loading UI before starting network work (React 18 batches updates across await).
      await new Promise((resolve) => {
        window.setTimeout(resolve, 0);
      });
      try {
        const categoryResponse = await hitBeApi(
          "categories?type=portfolio",
          "GET"
        );

        if (cancelled) return;

        if (categoryResponse.success) {
          setCategories(categoryResponse.data);
        }
      } catch (error) {
        if (cancelled) return;
        if (error.message.includes("Unauthorized")) {
          navigate("/");
          document.cookie =
            "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
          toast({
            title: "Error",
            description: "Unauthorized: Invalid token, please login once",
            status: "error",
            duration: 2000,
            isClosable: true,
          });
          return;
        }
        toast({
          title: "Error",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        if (!cancelled) {
          setIsPortfolioInitializing(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [navigate, toast]);

  useEffect(() => {
    if (selectedCategory?._id) {
      setTransactionCategoryId(selectedCategory._id);
    }
  }, [selectedCategory?._id]);

  useEffect(() => {
    if (!selectedCategory?._id) {
      setAnalyticsSubtreeCategories([]);
      setAnalyticsSelectedIds([]);
      setReportDateFrom("");
      setReportDateTo("");
      return;
    }
    setReportDateFrom("");
    setReportDateTo("");
    let cancelled = false;
    (async () => {
      setAnalyticsLoadingSubtree(true);
      try {
        const res = await hitBeApi(
          `portfolios/analytics/subtree-categories?rootCategoryId=${selectedCategory._id}`,
          "GET",
          null
        );
        if (cancelled || !res?.success) return;
        const cats = res.data?.categories ?? [];
        setAnalyticsSubtreeCategories(cats);
        setAnalyticsSelectedIds(cats.map((c) => String(c._id)));
      } catch (e) {
        if (!cancelled) {
          toast({
            title: "Analytics",
            description: e?.message || "Could not load category list.",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
        }
      } finally {
        if (!cancelled) setAnalyticsLoadingSubtree(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCategory?._id, toast]);

  useEffect(() => {
    if (!selectedCategory?._id) {
      setPortfolioDailyViz(null);
      setPortfolioChildrenViz(null);
      return undefined;
    }
    let cancelled = false;
    const id = selectedCategory._id;
    (async () => {
      setPortfolioFolderVizBusy(true);
      try {
        const { start, end } = portfolioAnalyticsRangeQuery;
        const rangeQuery =
          start && end
            ? `dateFrom=${encodeURIComponent(start)}&dateTo=${encodeURIComponent(end)}`
            : "";
        const q = [rangeQuery, `_cb=${Date.now()}`].filter(Boolean).join("&");
        const [dailyRes, kidsRes] = await Promise.all([
          hitBeApi(
            `portfolios/analytics/daily-flow?rootCategoryId=${encodeURIComponent(id)}${q ? `&${q}` : ""}`,
            "GET",
            null
          ),
          hitBeApi(
            `portfolios/analytics/immediate-children-totals?parentCategoryId=${encodeURIComponent(id)}${q ? `&${q}` : ""}`,
            "GET",
            null
          ),
        ]);
        if (cancelled) return;
        setPortfolioDailyViz(dailyRes?.success ? dailyRes.data : null);
        setPortfolioChildrenViz(kidsRes?.success ? kidsRes.data : null);
      } catch {
        if (!cancelled) {
          setPortfolioDailyViz(null);
          setPortfolioChildrenViz(null);
        }
      } finally {
        if (!cancelled) setPortfolioFolderVizBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCategory?._id, portfolioAnalyticsRangeQuery]);

  useEffect(() => {
    setActiveFolderTool(null);
  }, [selectedCategory?._id]);

  useEffect(() => {
    if (!selectedCategory?._id || activeFolderTool !== "pdf") {
      setSavedPortfolioReports([]);
      return;
    }
    let cancelled = false;
    listPortfolioReportsForRoot(selectedCategory._id)
      .then((rows) => {
        if (!cancelled) setSavedPortfolioReports(rows);
      })
      .catch(() => {
        if (!cancelled) setSavedPortfolioReports([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCategory?._id, activeFolderTool]);

  useEffect(() => {
    if (!isFlowCategoryBalanceOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsFlowCategoryBalanceOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFlowCategoryBalanceOpen]);

  useEffect(() => {
    if (!portfolioCombinedMode || !combinedLedgerRootCategory?._id) {
      setCombinedModeChildCategories([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await hitBeApi(
          `categories?parentId=${combinedLedgerRootCategory._id}`,
          "GET"
        );
        if (cancelled) return;
        if (r?.success && Array.isArray(r.data)) {
          setCombinedModeChildCategories(r.data);
        } else {
          setCombinedModeChildCategories([]);
        }
      } catch {
        if (!cancelled) setCombinedModeChildCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [portfolioCombinedMode, combinedLedgerRootCategory?._id]);

  useEffect(() => {
    if (!selectedCategory?._id || portfolioCombinedMode) {
      setPortfolioSiblingCategories([]);
      return;
    }
    const parentCategory =
      categoryPath.length >= 2
        ? categoryPath[categoryPath.length - 2]
        : null;
    if (!parentCategory?._id) {
      setPortfolioSiblingCategories(
        Array.isArray(categories) ? categories : []
      );
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await hitBeApi(
          `categories?parentId=${parentCategory._id}`,
          "GET"
        );
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          setPortfolioSiblingCategories(res.data);
        } else {
          setPortfolioSiblingCategories([]);
        }
      } catch {
        if (!cancelled) setPortfolioSiblingCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    selectedCategory?._id,
    categoryPath,
    portfolioCombinedMode,
    categories,
  ]);

  const transactionCategoryOptions = useMemo(() => {
    if (portfolioCombinedMode && combinedLedgerRootCategory?._id) {
      const root = combinedLedgerRootCategory;
      return [
        {
          id: root._id,
          label: `This folder — ${root.name}`,
        },
        ...combinedModeChildCategories.map((c) => ({
          id: c._id,
          label: c.name,
        })),
      ];
    }
    if (!selectedCategory?._id) return [];
    return [
      {
        id: selectedCategory._id,
        label: `This folder — ${selectedCategory.name}`,
      },
      ...subCategories.map((c) => ({ id: c._id, label: c.name })),
    ];
  }, [
    portfolioCombinedMode,
    combinedLedgerRootCategory,
    combinedModeChildCategories,
    selectedCategory,
    subCategories,
  ]);

  /** Edit row: same-level siblings + nested subfolders (e.g. move from B to D under parent A). */
  const editTransactionCategoryOptions = useMemo(() => {
    if (portfolioCombinedMode && combinedLedgerRootCategory?._id) {
      return transactionCategoryOptions;
    }
    if (!selectedCategory?._id) return [];

    const seen = new Set();
    const options = [];
    const push = (id, label) => {
      const sid = String(id ?? "");
      if (!sid || seen.has(sid)) return;
      seen.add(sid);
      options.push({ id, label });
    };

    for (const c of portfolioSiblingCategories) {
      const isCurrent = String(c._id) === String(selectedCategory._id);
      push(
        c._id,
        isCurrent ? `This folder — ${c.name}` : c.name
      );
    }

    push(
      selectedCategory._id,
      `This folder — ${selectedCategory.name}`
    );

    for (const c of subCategories) {
      push(c._id, `${c.name} (subfolder)`);
    }

    return options;
  }, [
    portfolioCombinedMode,
    combinedLedgerRootCategory,
    transactionCategoryOptions,
    selectedCategory,
    portfolioSiblingCategories,
    subCategories,
  ]);

  const selectedTransactionCategoryLabel = useMemo(() => {
    return (
      transactionCategoryOptions.find(
        (o) => String(o.id) === String(transactionCategoryId)
      )?.label ?? "Choose folder"
    );
  }, [transactionCategoryOptions, transactionCategoryId]);

  const filteredTransactionCategoryOptions = useMemo(() => {
    const q = transactionCategorySearch.trim().toLowerCase();
    if (!q) return transactionCategoryOptions;
    return transactionCategoryOptions.filter((o) =>
      o.label.toLowerCase().includes(q)
    );
  }, [transactionCategoryOptions, transactionCategorySearch]);

  /** Server returns newest first (date desc); do not reverse — reversing put oldest rows on top. */
  const sortedDisplayTransactions = useMemo(() => {
    if (portfolioCombinedMode) {
      return combinedLedgerTransactions;
    }
    return subTransactions;
  }, [portfolioCombinedMode, combinedLedgerTransactions, subTransactions]);

  const fetchMergedPortfolioLedger = useCallback(
    async (rootCategory, page = 1, append = false) => {
      if (!rootCategory?._id) return;
      setCombinedLedgerLoading(true);
      try {
        const res = await hitBeApi(
          `portfolios/transactions/subtree?rootCategoryId=${rootCategory._id}&page=${page}&limit=${PORTFOLIO_TX_PAGE_SIZE}`,
          "GET"
        );
        if (!res?.success) return;
        const raw = res.data;
        const items = raw?.items ?? [];
        setCombinedLedgerPage(raw?.page ?? page);
        setCombinedLedgerTotalPages(raw?.totalPages ?? 1);
        setCombinedLedgerTotal(raw?.total ?? 0);
        setCombinedCategoryNameById(raw?.categoryNameById ?? {});
        setCombinedLedgerTransactions((prev) =>
          append ? [...prev, ...items] : items
        );
      } catch (error) {
        toast({
          title: "Error",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setCombinedLedgerLoading(false);
      }
    },
    [toast]
  );

  const fetchMergedPortfolioLedgerRef = useRef(fetchMergedPortfolioLedger);
  fetchMergedPortfolioLedgerRef.current = fetchMergedPortfolioLedger;

  const openCombinedPortfolioView = async (rootCategory) => {
    if (!rootCategory?._id) return;
    setCombinedLedgerRootCategory(rootCategory);
    setPortfolioCombinedMode(true);
    await fetchMergedPortfolioLedger(rootCategory, 1, false);
  };

  const loadMoreCombinedLedger = () => {
    if (
      !combinedLedgerRootCategory ||
      combinedLedgerPage >= combinedLedgerTotalPages ||
      combinedLedgerLoading
    ) {
      return;
    }
    fetchMergedPortfolioLedger(
      combinedLedgerRootCategory,
      combinedLedgerPage + 1,
      true
    );
  };

  const closeCombinedPortfolioView = () => {
    setPortfolioCombinedMode(false);
    setCombinedLedgerRootCategory(null);
    setCombinedLedgerTransactions([]);
    setCombinedCategoryNameById({});
    setCombinedLedgerPage(1);
    setCombinedLedgerTotalPages(1);
    setCombinedLedgerTotal(0);
  };

  const refreshPortfolioCategoryTotalsInView = useCallback(async () => {
    const catResponse = await hitBeApi("categories?type=portfolio", "GET");
    let roots = [];
    if (catResponse.success && Array.isArray(catResponse.data)) {
      roots = catResponse.data;
      setCategories(roots);
    }

    if (categoryPath.length > 0) {
      const newPath = categoryPath.map((seg) => ({ ...seg }));
      const rootMatch = roots.find(
        (c) => String(c._id) === String(newPath[0]._id)
      );
      if (rootMatch) {
        newPath[0] = { ...newPath[0], ...rootMatch };
      }
      for (let i = 1; i < newPath.length; i++) {
        const parentRes = await hitBeApi(
          `categories?parentId=${newPath[i - 1]._id}`,
          "GET"
        );
        if (parentRes.success && Array.isArray(parentRes.data)) {
          const match = parentRes.data.find(
            (c) => String(c._id) === String(newPath[i]._id)
          );
          if (match) {
            newPath[i] = { ...newPath[i], ...match };
          }
        }
      }
      setCategoryPath(newPath);
      if (selectedCategory?._id) {
        const sel = newPath.find(
          (c) => String(c._id) === String(selectedCategory._id)
        );
        if (sel) {
          setSelectedCategory((prev) => ({ ...prev, ...sel }));
        }
      }
    }

    if (selectedCategory?._id) {
      const [subCatResponse, subTxResponse] = await Promise.all([
        hitBeApi(`categories?parentId=${selectedCategory._id}`, "GET"),
        hitBeApi(
          `portfolios/transactions?categoryId=${selectedCategory._id}&page=1&limit=${PORTFOLIO_TX_PAGE_SIZE}&paginated=true`,
          "GET"
        ),
      ]);
      if (subCatResponse.success) {
        setSubCategories(subCatResponse.data);
      }
      if (subTxResponse.success) {
        const txPage = parsePortfolioTransactionsResponse(subTxResponse);
        setSubTransactions(txPage.items);
        setSubTransactionsPage(txPage.page);
        setSubTransactionsTotalPages(txPage.totalPages);
        setSubTransactionsTotal(txPage.total);
      }
    }

    if (
      portfolioCombinedMode &&
      (combinedLedgerRootCategory || selectedCategory)
    ) {
      await fetchMergedPortfolioLedgerRef.current?.(
        combinedLedgerRootCategory || selectedCategory,
        1,
        false
      );
    }
  }, [
    categoryPath,
    selectedCategory,
    portfolioCombinedMode,
    combinedLedgerRootCategory,
  ]);

  const refreshPortfolioAfterVoiceMutation =
    refreshPortfolioCategoryTotalsInView;

  const closeFlowBucketInfo = () => {
    setFlowBucketInfoOpen(false);
    setFlowBucketInfoRows([]);
    setFlowBucketInfoRange({ start: "", end: "" });
    setFlowBucketInfoMode("cashIn");
    setFlowBucketInfoTruncated(false);
  };

  const openFlowBucketInfo = useCallback(
    async (mode, datum) => {
      if (!selectedCategory?._id || !datum?.bucketStart) return;
      const start = datum.bucketStart;
      const end = datum.bucketEnd || datum.bucketStart;
      setFlowBucketInfoRange({ start, end });
      setFlowBucketInfoMode(mode);
      setFlowBucketInfoOpen(true);
      setFlowBucketInfoLoading(true);
      setFlowBucketInfoRows([]);
      setFlowBucketInfoTruncated(false);
      try {
        const res = await hitBeApi("portfolios/analytics/data", "POST", {
          rootCategoryId: selectedCategory._id,
          categoryIds: [],
          mode: "detail",
          dateFrom: start,
          dateTo: end,
        });
        if (!res?.success) {
          throw new Error(res?.message || "Could not load bucket activity.");
        }
        const details = Array.isArray(res.data?.details) ? res.data.details : [];
        const rows = [];
        details.forEach((section) => {
          const folder = section.folder || "—";
          const items = Array.isArray(section.rows) ? section.rows : [];
          items.forEach((row) => {
            rows.push({
              ...row,
              folder,
              cashIn: Number(row.cashIn) || 0,
              cashOut: Number(row.cashOut) || 0,
            });
          });
        });
        const filtered = rows.filter((row) => {
          if (mode === "cashIn") return Number(row.cashIn) > 0;
          return Number(row.cashOut) > 0;
        });
        setFlowBucketInfoRows(filtered);
        setFlowBucketInfoTruncated(!!res.data?.truncated);
      } catch (error) {
        toast({
          title: "Bucket load failed",
          description: error?.message || "Try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setFlowBucketInfoRows([]);
      } finally {
        setFlowBucketInfoLoading(false);
      }
    },
    [selectedCategory?._id, toast]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!isFlowCategoryBalanceOpen) return;
      if (!flowHoverDatum?.bucketStart) return;
      const key = String(event.key || "").toLowerCase();
      if (key === "i") {
        event.preventDefault();
        openFlowBucketInfo("cashIn", flowHoverDatum);
      }
      if (key === "o") {
        event.preventDefault();
        openFlowBucketInfo("cashOut", flowHoverDatum);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFlowCategoryBalanceOpen, flowHoverDatum, openFlowBucketInfo]);

  useEffect(() => {
    if (!transactionCategoryPicker.isOpen) return;
    const id = window.setTimeout(() => {
      transactionCategorySearchRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [transactionCategoryPicker.isOpen]);

  const [loadingElapsedSeconds, setLoadingElapsedSeconds] = useState(0);

  useEffect(() => {
    const loading = isPortfolioInitializing || isCategoryBeingSelected;
    if (!loading) {
      setLoadingElapsedSeconds(0);
      return;
    }
    setLoadingElapsedSeconds(0);
    const interval = window.setInterval(() => {
      setLoadingElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [isPortfolioInitializing, isCategoryBeingSelected]);

  // Add new category or subcategory
  const handleAddCategory = async () => {
    if (!categoryName) {
      toast({
        title: "Error",
        description: "Please enter a name.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const createCategoryResponse = await hitBeApi("categories", "POST", {
        name: categoryName,
        type: "portfolio",
        parentId: selectedCategory?._id,
      });

      if (selectedCategory && createCategoryResponse.success) {
        setSubCategories([...subCategories, createCategoryResponse.data]);
      } else {
        setCategories([...categories, createCategoryResponse.data]);
      }

      toast({
        title: "Success",
        description: "Category added successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setCategoryName("");
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCategoryUpdate = async (
    portfolioCategoryId,
    updatedPortfolioCategoryName
  ) => {
    if (!portfolioCategoryId || updatedPortfolioCategoryName.length === 0) {
      toast({
        title: "Error",
        description: "Portfolio Category ID and updated name are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const updatePortfolioCategoryResponse = await hitBeApi(
        `categories/${portfolioCategoryId}`,
        "PUT",
        {
          name: updatedPortfolioCategoryName,
        }
      );

      if (updatePortfolioCategoryResponse.success) {
        // Update the portfolio category in the list
        if (selectedCategory) {
          const updatedSubCategories = subCategories.map((category) =>
            category._id === portfolioCategoryId
              ? { ...category, name: updatedPortfolioCategoryName }
              : category
          );
          setSubCategories(updatedSubCategories);
          setIsEditing(null); // Exit editing mode
        } else {
          const updatedCategories = categories.map((category) =>
            category._id === portfolioCategoryId
              ? { ...category, name: updatedPortfolioCategoryName }
              : category
          );
          setCategories(updatedCategories);
          setIsEditing(null); // Exit editing mode
        }

        toast({
          title: "Success",
          description: "Portfolio category updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePortfolioHideAmountSumToggle = async (categoryId, nextHidden) => {
    if (!categoryId) return;
    setHideAmountSavingId(String(categoryId));
    try {
      const res = await hitBeApi(`categories/${categoryId}`, "PUT", {
        portfolioHideAmountSum: !!nextHidden,
      });
      if (!res?.success) {
        throw new Error(res?.message || "Could not update folder.");
      }
      const updated = res.data;
      const hidden = !!updated?.portfolioHideAmountSum;
      const patch = (c) =>
        String(c._id) === String(categoryId)
          ? { ...c, portfolioHideAmountSum: hidden }
          : c;
      setCategories((prev) => prev.map(patch));
      setSubCategories((prev) => prev.map(patch));
      setCategoryPath((prev) => prev.map(patch));
      setSelectedCategory((prev) =>
        prev && String(prev._id) === String(categoryId)
          ? { ...prev, portfolioHideAmountSum: hidden }
          : prev
      );
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setHideAmountSavingId(null);
    }
  };

  const onPortfolioRootDragStart = useCallback((categoryId) => {
    setDraggingPortfolioRootId(String(categoryId));
  }, []);

  const onPortfolioRootFolderDrop = useCallback(
    (e, targetId) => {
      e.preventDefault();
      const sourceId = e.dataTransfer.getData("text/plain");
      if (!sourceId || String(sourceId) === String(targetId)) {
        setDraggingPortfolioRootId(null);
        return;
      }
      setPortfolioRootOrderSaving(true);
      setCategories((prev) => {
        const next = reorderPortfolioRootFolders(prev, sourceId, targetId);
        if (!next) {
          setPortfolioRootOrderSaving(false);
          setDraggingPortfolioRootId(null);
          return prev;
        }
        const snapshot = [...prev];
        (async () => {
          try {
            const res = await hitBeApi(
              "categories/portfolio/root-order",
              "PUT",
              { orderedIds: next.map((c) => c._id) }
            );
            if (!res?.success) {
              throw new Error(res?.message || "Could not save folder order.");
            }
          } catch (err) {
            setCategories(snapshot);
            toast({
              title: "Could not save order",
              description: err?.message || "Try again.",
              status: "error",
              duration: 4000,
              isClosable: true,
            });
          } finally {
            setPortfolioRootOrderSaving(false);
            setDraggingPortfolioRootId(null);
          }
        })();
        return next;
      });
    },
    [toast]
  );

  const handleCategoryDelete = async (portfolioCategoryId) => {
    if (!portfolioCategoryId) {
      toast({
        title: "Error",
        description: "Portfolio Category ID is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const deletePortfolioCategoryResponse = await executeCategoryDelete(
        portfolioCategoryId
      );
      if (deletePortfolioCategoryResponse.success) {
        toast({
          title: "Success",
          description: "Portfolio category deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      if (error.message.includes("Items associated with this category")) {
        setPendingForceDeleteCategoryId(portfolioCategoryId);
        openForceDeleteDialog();
        return;
      }
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleForceDeleteCategory = async () => {
    if (!pendingForceDeleteCategoryId) {
      return;
    }
    try {
      const deletePortfolioCategoryResponse = await executeCategoryDelete(
        pendingForceDeleteCategoryId,
        true
      );
      if (deletePortfolioCategoryResponse.success) {
        toast({
          title: "Success",
          description:
            "Category and all associated items were deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      closeForceDeleteDialog();
      setPendingForceDeleteCategoryId(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Add new category or subcategory
  const handleAddTransaction = async () => {
    if (!transactionName || !transactionAmount) {
      toast({
        title: "Error",
        description: "Please enter a name and amount.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const amountNum = parsePositiveAmount(transactionAmount);
    if (amountNum == null) {
      toast({
        title: "Error",
        description: "Enter a valid amount greater than zero.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const targetCategoryId =
      transactionCategoryId || selectedCategory?._id || null;
    if (!targetCategoryId) {
      toast({
        title: "Error",
        description: "Please select a category.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setTransactionSupportingDocsBusy(true);
      let fileUrls = [];
      if (pendingTransactionSupportingFiles.length > 0) {
        try {
          for (const file of pendingTransactionSupportingFiles) {
            const url = await uploadPortfolioSupportingFileToAws(file);
            fileUrls.push(url);
          }
        } catch (uploadErr) {
          toast({
            title: "Upload failed",
            description: uploadErr.message,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
          return;
        }
      }

      const createTransactionResponse = await hitBeApi(
        "portfolios/transactions",
        "POST",
        {
          desc: transactionName,
          categoryId: targetCategoryId,
          amount: amountNum,
          ...newTransactionSourceDateTime(),
          ...cashFieldsForPortfolioAmount(amountNum, newTransactionCashFlow),
          ...(fileUrls.length > 0 ? { fileUrls } : {}),
        }
      );

      if (createTransactionResponse.success) {
        appStore.dispatch({ type: "CLEAR_API_CACHE" });
        await refreshPortfolioCategoryTotalsInView();

        toast({
          title: "Success",
          description: "Transaction added successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setTransactionAmount("");
        setTransactionName("");
        setNewTransactionCashFlow("out");
        setPendingTransactionSupportingFiles([]);
      } else {
        toast({
          title: "Error",
          description:
            createTransactionResponse?.message || "Could not save transaction.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setTransactionSupportingDocsBusy(false);
    }
  };

  const handleTransactionUpdate = async (
    transactionId,
    updatedDescription,
    updatedAmount,
    updatedCategoryId
  ) => {
    if (updatedDescription.length === 0 || !updatedAmount || !transactionId) {
      toast({
        title: "Error",
        description: "All fields are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const amountNum = parsePositiveAmount(updatedAmount);
      if (amountNum == null) {
        toast({
          title: "Error",
          description: "Enter a valid amount greater than zero.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const existing =
        subTransactions.find((t) => t._id === transactionId) ??
        combinedLedgerTransactions.find((t) => t._id === transactionId);

      const payload = {
        desc: updatedDescription,
        amount: amountNum,
        ...cashFieldsForPortfolioUpdate(existing, amountNum),
      };
      const cat =
        updatedCategoryId != null ? String(updatedCategoryId).trim() : "";
      if (cat && isLikelyMongoObjectId(cat)) {
        payload.categoryId = cat;
      }

      const updateTransactionResponse = await hitBeApi(
        `portfolios/transactions/${transactionId}`,
        "PUT",
        payload
      );

      if (updateTransactionResponse.success) {
        setIsEditing(null);
        appStore.dispatch({ type: "CLEAR_API_CACHE" });
        try {
          await refreshPortfolioCategoryTotalsInView();
        } catch {
          /* list refresh is best-effort */
        }

        toast({
          title: "Success",
          description: "Transaction updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleTransactionDelete = async (transactionId) => {
    if (!transactionId) {
      toast({
        title: "Error",
        description: "Transaction ID is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const deleteTransactionResponse = await hitBeApi(
        `portfolios/transactions/${transactionId}`,
        "DELETE"
      );

      if (deleteTransactionResponse.success) {
        setIsEditing(null);
        appStore.dispatch({ type: "CLEAR_API_CACHE" });
        try {
          await refreshPortfolioCategoryTotalsInView();
        } catch {
          /* totals refresh is best-effort */
        }

        toast({
          title: "Success",
          description: "Transaction soft deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCategorySelect = async (
    category,
    isDirectClick = false,
    directClickIndex = 0
  ) => {
    if (categoryNavInFlightRef.current) {
      return;
    }
    categoryNavInFlightRef.current = true;
    flushSync(() => {
      setIsCategoryBeingSelected(true);
    });
    await new Promise((resolve) => {
      window.setTimeout(resolve, 0);
    });
    try {
      closeCombinedPortfolioView();

      const [subCategoryResponse, subTransactionsResponse] = await Promise.all([
        hitBeApi(`categories?parentId=${category._id}`, "GET"),
        hitBeApi(
          `portfolios/transactions?categoryId=${category._id}&page=1&limit=${PORTFOLIO_TX_PAGE_SIZE}&paginated=true`,
          "GET"
        ),
      ]);

      setSelectedCategory(category);
      if (!isDirectClick) {
        setCategoryPath((prev) => {
          const last = prev[prev.length - 1];
          if (last?._id === category._id) {
            return prev;
          }
          return [...prev, category];
        });
      } else {
        setCategoryPath((prev) => [...prev.slice(0, directClickIndex + 1)]);
      }
      setSubCategories(subCategoryResponse.data);
      const txPage = parsePortfolioTransactionsResponse(subTransactionsResponse);
      setSubTransactions(txPage.items);
      setSubTransactionsPage(txPage.page);
      setSubTransactionsTotalPages(txPage.totalPages);
      setSubTransactionsTotal(txPage.total);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      categoryNavInFlightRef.current = false;
      setIsCategoryBeingSelected(false);
    }
  };

  const handleBackToCategories = () => {
    closeCombinedPortfolioView();
    setSelectedCategory(null);
    setCategoryPath([]);
    setSubTransactions([]);
    setSubCategories([]);
    setSubTransactionsPage(1);
    setSubTransactionsTotalPages(1);
    setSubTransactionsTotal(0);
    setAnalyticsSubtreeCategories([]);
    setAnalyticsSelectedIds([]);
    setAnalyticsMode("summary");
    setReportDateFrom("");
    setReportDateTo("");
    setActiveFolderTool(null);
    setSavedPortfolioReports([]);
    setPendingPdfSave(null);
    setPortfolioSearchQuery("");
    setPortfolioSearchRows([]);
    setPortfolioSearchTotal(0);
    setPortfolioSearchTotalPages(1);
    setPortfolioSearchPage(1);
    setPortfolioSearchScopeLabel("");
    setPortfolioSearchMatchDescription(true);
    setPortfolioSearchMatchCategory(true);
  };

  const handleCloseCategoryView = () => {
    closeCombinedPortfolioView();
    if (!selectedCategory || categoryPath.length <= 1) {
      handleBackToCategories();
      return;
    }
    const parentIndex = categoryPath.length - 2;
    const parentCategory = categoryPath[parentIndex];
    handleCategorySelect(parentCategory, true, parentIndex);
  };

  const toggleAnalyticsCategory = (id) => {
    const s = String(id);
    setAnalyticsSelectedIds((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const selectAllAnalyticsCategories = () => {
    setAnalyticsSelectedIds(
      analyticsSubtreeCategories.map((c) => String(c._id))
    );
  };

  const applyAnalyticsPresetLast7Days = () => {
    const end = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - 6);
    setReportDateFrom(localDateInputValue(start));
    setReportDateTo(localDateInputValue(end));
  };

  const clearAnalyticsDateRange = () => {
    setReportDateFrom("");
    setReportDateTo("");
  };

  const handleAnalyticsDownloadPdf = async () => {
    if (!selectedCategory?._id) return;
    if (!analyticsSelectedIds.length) {
      toast({
        title: "Select categories",
        description: "Pick at least one folder to include in the report.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const df = String(reportDateFrom || "").trim();
    const dt = String(reportDateTo || "").trim();
    const iso = /^\d{4}-\d{2}-\d{2}$/;
    if ((df && !iso.test(df)) || (dt && !iso.test(dt))) {
      toast({
        title: "Invalid dates",
        description: "Use the date picker so dates are in YYYY-MM-DD form.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (df && dt && df > dt) {
      toast({
        title: "Invalid range",
        description: "From date must be on or before to date.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setAnalyticsPdfBusy(true);
    try {
      const body = {
        rootCategoryId: selectedCategory._id,
        categoryIds: analyticsSelectedIds,
        mode: analyticsMode,
      };
      if (df) body.dateFrom = df;
      if (dt) body.dateTo = dt;
      const res = await hitBeApi("portfolios/analytics/data", "POST", body);
      if (!res?.success) {
        throw new Error(res?.message || "Could not load report data.");
      }
      const dateTag =
        df || dt
          ? `-${df || "start"}_${dt || "end"}`
          : "";
      const baseName = `portfolio-${(selectedCategory.name || "report")
        .replace(/[^\w-]+/g, "-")
        .slice(0, 40)}${dateTag}`;
      const { blob, fileName } = downloadPortfolioAnalyticsPdf(
        res.data,
        baseName
      );
      setPendingPdfSave({
        blob,
        fileName,
        reportData: res.data,
        categoryIdsKey: [
          [...analyticsSelectedIds].sort().join(","),
          df || "",
          dt || "",
        ].join("#"),
      });
      onOpenSaveReportDialog();
    } catch (e) {
      toast({
        title: "PDF failed",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setAnalyticsPdfBusy(false);
    }
  };

  const confirmSaveReportToDevice = async () => {
    if (!pendingPdfSave || !selectedCategory?._id) {
      onCloseSaveReportDialog();
      setPendingPdfSave(null);
      return;
    }
    try {
      await savePortfolioReport({
        rootCategoryId: selectedCategory._id,
        rootName: selectedCategory.name,
        mode: analyticsMode,
        categoryIdsKey: pendingPdfSave.categoryIdsKey,
        blob: pendingPdfSave.blob,
        reportData: pendingPdfSave.reportData,
      });
      const rows = await listPortfolioReportsForRoot(selectedCategory._id);
      setSavedPortfolioReports(rows);
      toast({
        title: "Saved on this device",
        description: "Reopen it from Saved reports without regenerating.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "Could not save",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      onCloseSaveReportDialog();
      setPendingPdfSave(null);
    }
  };

  const skipSaveReport = () => {
    onCloseSaveReportDialog();
    setPendingPdfSave(null);
  };

  const handleDeleteSavedReport = async (id) => {
    try {
      await deletePortfolioReport(id);
      if (selectedCategory?._id) {
        const rows = await listPortfolioReportsForRoot(selectedCategory._id);
        setSavedPortfolioReports(rows);
      }
      toast({ title: "Report removed", status: "info", duration: 2000 });
    } catch (e) {
      toast({
        title: "Could not remove",
        description: e?.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const loadMoreSubTransactions = async () => {
    if (
      !selectedCategory?._id ||
      portfolioCombinedMode ||
      subTransactionsPage >= subTransactionsTotalPages
    ) {
      return;
    }
    try {
      const res = await hitBeApi(
        `portfolios/transactions?categoryId=${selectedCategory._id}&page=${
          subTransactionsPage + 1
        }&limit=${PORTFOLIO_TX_PAGE_SIZE}&paginated=true`,
        "GET"
      );
      const txPage = parsePortfolioTransactionsResponse(res);
      setSubTransactions((prev) => [...prev, ...txPage.items]);
      setSubTransactionsPage(txPage.page);
      setSubTransactionsTotalPages(txPage.totalPages);
      setSubTransactionsTotal(txPage.total);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const stopPortfolioAssistantSpeech = () => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setPortfolioVoiceSpeaking(false);
  };

  const handleClearVoiceAssistantCache = async () => {
    stopPortfolioAssistantSpeech();
    clearClientApiCache();
    setPortfolioVoiceReply("");
    setPortfolioVoiceDraft("");
    portfolioVoicePendingRef.current = null;
    setPortfolioVoicePending(null);
    setPortfolioVoiceSuggested([]);
    setPortfolioVoiceCacheClearing(true);
    try {
      await hitBeApi("portfolios/voice-assistant/cache-clear", "POST", {});
      toast({
        title: "Cache cleared",
        description:
          "Saved API responses were dropped and the last assistant reply was cleared. Ask again for a fresh answer.",
        status: "success",
        duration: 4500,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "App cache cleared",
        description:
          e?.message ||
          "Local cache was cleared; the server confirm step failed (you can still retry).",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPortfolioVoiceCacheClearing(false);
    }
  };

  const stopPortfolioVoiceDictation = ({ capture = false } = {}) => {
    const handle = portfolioVoiceDictationRef.current;
    if (handle) {
      if (capture && typeof handle.stopAndCapture === "function") {
        handle.stopAndCapture();
      } else {
        handle.abort();
      }
    }
    portfolioVoiceDictationRef.current = null;
    setPortfolioVoiceListening(false);
  };

  const runPortfolioVoiceCaptureOnce = () => {
    stopPortfolioAssistantSpeech();
    stopPortfolioVoiceDictation();
    portfolioVoiceDictationRef.current = startBrowserVoiceDictation({
      lang: portfolioSttLang || getPreferredSttLang(),
      continuous: true,
      silenceTimeoutMs: VOICE_DICTATION_SILENCE_MS,
      categoryNames: portfolioSpeechCategoryNames,
      cleanupTranscript: (t) =>
        cleanupPortfolioSpeechTranscript(t, {
          categoryNames: portfolioSpeechCategoryNames,
        }),
      onListeningStart: () => setPortfolioVoiceListening(true),
      onInterim: (t) => setPortfolioVoiceDraft(t),
      onListeningEnd: () => setPortfolioVoiceListening(false),
      onFinal: (t) => {
        portfolioVoiceDictationRef.current = null;
        const captured = String(t || "").trim();
        setPortfolioVoiceDraft(captured);
        if (portfolioVoiceSkipAutoSendRef.current) {
          portfolioVoiceSkipAutoSendRef.current = false;
          return;
        }
        if (captured) {
          submitPortfolioVoiceAssistantRef.current(captured);
        } else {
          toast({
            title: "No speech detected",
            description:
              "Try again, speak closer to the mic, or type your message below.",
            status: "info",
            duration: 4000,
            isClosable: true,
          });
        }
      },
      onError: (ev) => {
        portfolioVoiceDictationRef.current = null;
        setPortfolioVoiceListening(false);
        const code = ev?.error;
        if (code === "not-allowed") {
          toast({
            title: "Microphone blocked",
            description: "Allow microphone access for this site in browser settings.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        } else if (code !== "no-speech" && code !== "aborted") {
          toast({
            title: "Voice error",
            description:
              "Could not capture speech. Type your message or try the mic again.",
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
        }
      },
    });
  };

  const speakPortfolioAssistantReply = (replyText, openMicAfterFollowUp) => {
    if (typeof window === "undefined" || !replyText) return;
    try {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      setPortfolioVoiceSpeaking(false);
      speakBrowserAssistantReply(replyText, {
        lang: "en-IN",
        flushDelayMs: 0,
        onBeforeSpeak: () => setPortfolioVoiceSpeaking(true),
        onEnd: () => {
          setPortfolioVoiceSpeaking(false);
          if (!openMicAfterFollowUp) return;
          setTimeout(() => runPortfolioVoiceCaptureOnce(), 400);
        },
        onError: () => {
          setPortfolioVoiceSpeaking(false);
          if (openMicAfterFollowUp) {
            setTimeout(() => runPortfolioVoiceCaptureOnce(), 400);
          }
        },
      });
    } catch {
      setPortfolioVoiceSpeaking(false);
      if (openMicAfterFollowUp) {
        setTimeout(() => runPortfolioVoiceCaptureOnce(), 400);
      }
    }
  };

  useEffect(() => {
    return () => {
      portfolioVoiceDictationRef.current?.abort();
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const submitPortfolioVoiceAssistant = async (overrideText) => {
    const raw =
      overrideText !== undefined && overrideText !== null
        ? String(overrideText)
        : portfolioVoiceDraft;
    const text = raw.trim();
    if (!text) {
      toast({
        title: "Nothing to send",
        description: "Type or dictate what you want to add.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    stopPortfolioAssistantSpeech();
    stopPortfolioVoiceDictation();
    setPortfolioVoiceLoading(true);
    try {
      const res = await hitBeApi("portfolios/voice-assistant", "POST", {
        text,
        selectedCategoryId: selectedCategory?._id ?? null,
        selectedCategoryName: selectedCategory?.name ?? null,
        pendingTransaction: portfolioVoicePendingRef.current,
      });
      if (res.success && res.data) {
        if (res.data.userTokenDetails) {
          const currentUser = appStore.getState().user || {};
          appStore.dispatch({
            type: "SET_USER",
            value: { ...currentUser, userTokenDetails: res.data.userTokenDetails },
          });
        }
        const reply = res.data.reply || "";
        const pending = res.data.pendingTransaction ?? null;
        const suggested = res.data.suggestedSubfolders ?? [];
        portfolioVoicePendingRef.current = pending;
        setPortfolioVoicePending(pending);
        setPortfolioVoiceSuggested(Array.isArray(suggested) ? suggested : []);
        setPortfolioVoiceReply(reply);
        const createdTxCount =
          res.data.actions?.filter((a) => a.type === "transaction_created")
            .length ?? 0;
        const deletedTxCount =
          res.data.actions?.filter((a) => a.type === "transactions_deleted")
            .length ?? 0;
        const updatedTxCount =
          res.data.actions?.filter((a) => a.type === "transaction_updated")
            .length ?? 0;
        const createdCategoryCount =
          res.data.actions?.filter((a) => a.type === "category_created")
            .length ?? 0;
        const portfolioDataChanged =
          createdTxCount > 0 ||
          deletedTxCount > 0 ||
          updatedTxCount > 0 ||
          createdCategoryCount > 0;

        if (portfolioDataChanged) {
          clearClientApiCache();
          await refreshPortfolioAfterVoiceMutation();
          portfolioVoicePendingRef.current = null;
          setPortfolioVoicePending(null);
          setPortfolioVoiceSuggested([]);
        }

        const createdTx = createdTxCount > 0;
        const looksLikeRecordedReply =
          /\brecorded\b|\bsaved\b|\badded\b|\bcreated\b/i.test(reply || "");
        toast({
          title: createdTx
            ? createdTxCount > 1
              ? `${createdTxCount} entries recorded`
              : "Recorded"
            : pending
              ? "Choose a folder"
              : portfolioDataChanged
                ? "Portfolio updated"
                : "Portfolio assistant",
          description: reply || "Done.",
          status: createdTx
            ? "success"
            : pending
              ? "info"
              : portfolioDataChanged
                ? "success"
                : looksLikeRecordedReply
                  ? "warning"
                  : "info",
          duration: createdTxCount > 1 ? 7000 : 4500,
          isClosable: true,
        });
        if (reply) {
          speakPortfolioAssistantReply(reply, !!pending && !createdTx);
        }
      }
    } catch (error) {
      toast({
        title: "Assistant error",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setPortfolioVoiceLoading(false);
    }
  };

  submitPortfolioVoiceAssistantRef.current = submitPortfolioVoiceAssistant;

  const cancelPortfolioVoiceListening = () => {
    portfolioVoiceSkipAutoSendRef.current = true;
    stopPortfolioVoiceDictation();
    toast({
      title: "Listening stopped",
      description: "Edit the text below or tap the mic to record again.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const finishPortfolioVoiceListening = () => {
    if (!portfolioVoiceListening) return;
    portfolioVoiceSkipAutoSendRef.current = false;
    stopPortfolioVoiceDictation({ capture: true });
  };

  const startPortfolioVoiceRecognition = () => {
    if (!getSpeechRecognitionClass()) {
      toast({
        title: "Voice not supported",
        description: "Use Chrome or Edge, or type your message below.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (portfolioVoiceListening || portfolioVoiceLoading) {
      return;
    }
    portfolioVoiceSkipAutoSendRef.current = false;
    runPortfolioVoiceCaptureOnce();
  };

  const handleImportPortfolioCsv = async () => {
    if (!importParentCategoryName.trim() || !importFile) {
      toast({
        title: "Missing details",
        description: "Please provide parent category name and CSV file.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsImportingCsv(true);
      const formData = new FormData();
      formData.append("parentCategoryName", importParentCategoryName.trim());
      formData.append("file", importFile);

      const token = getCookieToken();
      const response = await axios.post(
        `${serverBaseUrl}/portfolios/transactions/import-csv`,
        formData,
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data?.success) {
        appStore.dispatch({ type: "CLEAR_API_CACHE" });
        const categoryResponse = await hitBeApi(
          "categories?type=portfolio",
          "GET"
        );
        if (categoryResponse.success) {
          setCategories(categoryResponse.data);
        }

        toast({
          title: "CSV imported",
          description: `Created ${response.data.data.createdSubCategoryCount} subcategories and ${response.data.data.createdTransactionCount} entries.`,
          status: "success",
          duration: 3500,
          isClosable: true,
        });
        setImportFile(null);
        setImportParentCategoryName("");
        closeImportModal();
      }
    } catch (error) {
      toast({
        title: "Import failed",
        description:
          error?.response?.data?.message || error.message || "Please retry.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsImportingCsv(false);
    }
  };

  return (
    <>
      {(isPortfolioInitializing || isCategoryBeingSelected) && (
        <Portal>
          <Flex
            position="fixed"
            inset={0}
            zIndex={2000}
            bg={loadingOverlayBg}
            align="center"
            justify="center"
            direction="column"
            gap={4}
            pointerEvents="auto"
            aria-busy="true"
            aria-live="polite"
          >
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="teal.500"
              size="xl"
            />
            <VStack spacing={1} px={4}>
              <Text fontSize="sm" fontWeight="medium" color={textColor}>
                {isPortfolioInitializing
                  ? "Loading portfolio data…"
                  : "Fetching category data…"}
              </Text>
              <Text
                fontSize="lg"
                fontWeight="semibold"
                color="teal.600"
                fontVariantNumeric="tabular-nums"
                letterSpacing="wide"
              >
                {loadingElapsedSeconds}s
              </Text>
            </VStack>
          </Flex>
        </Portal>
      )}
      <Box minH="100vh" bg={pageBg} py={{ base: 4, md: 8 }} position="relative">
        <Container maxW="container.xl" px={{ base: 3, md: 6 }}>
          <VStack spacing={{ base: 5, md: 7 }} align="stretch">
            <Card
              overflow="hidden"
              borderRadius="2xl"
              boxShadow="lg"
              borderWidth="1px"
              borderColor={mutedLine}
              bg={surfaceCard}
            >
              <Box
                bgGradient="linear(to-r, blue.600, purple.600)"
                px={{ base: 5, md: 8 }}
                py={{ base: 6, md: 7 }}
              >
                <Flex
                  align="flex-start"
                  justify="space-between"
                  gap={4}
                  direction={{ base: "column", sm: "row" }}
                >
                  <Flex
                    align="flex-start"
                    gap={4}
                    direction={{ base: "column", sm: "row" }}
                    flex="1"
                    minW={0}
                  >
                    <Flex
                      align="center"
                      justify="center"
                      w={12}
                      h={12}
                      borderRadius="xl"
                      bg="whiteAlpha.200"
                      flexShrink={0}
                    >
                      <Icon as={FiTrendingUp} boxSize={7} color="white" />
                    </Flex>
                    <Box textAlign="left">
                      <Heading as="h1" size="lg" color="white" fontWeight="extrabold" letterSpacing="tight">
                        Portfolio
                      </Heading>
                      <Text
                        fontSize="sm"
                        color="whiteAlpha.900"
                        mt={2}
                        maxW="3xl"
                        lineHeight="tall"
                      >
                        Cashbook-style folders, transactions, search, analytics, and voice
                        commands — consistent with the rest of Manage360.
                      </Text>
                    </Box>
                  </Flex>
                  <TabCacheButton tabId="portfolio" onGradient />
                </Flex>
              </Box>
            </Card>

            <Card
              borderRadius="xl"
              borderWidth="1px"
              borderColor={borderColor}
              bg={boxBgColor}
              boxShadow="sm"
              overflow="hidden"
            >
              <CardHeader
                pb={2}
                borderBottomWidth="1px"
                borderColor={mutedLine}
                bg={assistantHeaderBg}
              >
                <HStack spacing={3}>
                  <Icon as={FiMic} color={headingColor} boxSize={5} />
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" color={headingColor}>
                      Voice &amp; text assistant
                    </Text>
                    <Text fontSize="xs" color={subtleLabel}>
                      Speak or type to add transactions or create folders.
                    </Text>
                  </Box>
                </HStack>
              </CardHeader>
              <CardBody px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }} textAlign="left">
        <Text fontSize="xs" color="gray.500" mb={3}>
          Speak or type to add a transaction or create a folder. When you open a
          category, new entries default there unless you name another folder.
          Say &quot;top level&quot; to create a root folder, or &quot;under SBI
          transactions&quot; for a subfolder. If the assistant asks which
          subfolder, your reply is read aloud and the mic turns on so you can say
          the folder name, &quot;first&quot; / &quot;second&quot; / &quot;third&quot;,
          or &quot;main folder only&quot;. One long sentence with several amounts
          (salary in, card payment, transfers) can create multiple entries — only
          folders that already exist in your portfolio are used. Speech uses your
          browser (Chrome or Edge recommended) — no AI Usage Credits for dictation; pick
          English (India) for best INR and bank names.
        </Text>
        <FormControl size="sm" mb={3} maxW="xs">
          <FormLabel fontSize="xs" mb={1} color={subtleLabel}>
            Speech language (browser)
          </FormLabel>
          <Select
            value={portfolioSttLang}
            onChange={(e) => {
              const v = e.target.value;
              setPortfolioSttLang(v);
              setPreferredSttLang(v);
            }}
            bg={inputBgColor}
            borderColor={borderColor}
            isDisabled={portfolioVoiceListening}
          >
            {PORTFOLIO_STT_LANG_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormControl>
        <Textarea
          value={portfolioVoiceDraft}
          onChange={(e) => setPortfolioVoiceDraft(e.target.value)}
          placeholder='Example: "Received 50000 salary in HDFC bank, spent 12000 from HDFC to pay HDFC credit card"'
          size="sm"
          rows={3}
          bg={inputBgColor}
          borderColor={borderColor}
          _focus={{ borderColor: focusBorderColor, boxShadow: "outline" }}
          mb={3}
        />
        <HStack spacing={3} flexWrap="wrap" align="center">
          <Button
            size="sm"
            colorScheme="teal"
            onClick={() => submitPortfolioVoiceAssistant()}
            isLoading={portfolioVoiceLoading}
            loadingText="Sending"
          >
            Send to assistant
          </Button>
          {portfolioVoiceListening ? (
            <HStack spacing={2} flexWrap="wrap">
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                leftIcon={<CloseIcon boxSize={3} />}
                onClick={cancelPortfolioVoiceListening}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="teal"
                onClick={finishPortfolioVoiceListening}
              >
                Done speaking
              </Button>
            </HStack>
          ) : (
            <VoiceAssistantMicButton
              colorScheme="teal"
              compact
              tooltipLabel="Start voice dictation (browser — no AI Usage Credits). Use Cancel while listening to stop without sending."
              onClick={startPortfolioVoiceRecognition}
              isDisabled={portfolioVoiceLoading || portfolioVoiceSpeaking}
            />
          )}
          {portfolioVoiceSpeaking ? (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              leftIcon={<Icon as={FiVolumeX} />}
              onClick={stopPortfolioAssistantSpeech}
            >
              Stop speaking
            </Button>
          ) : null}
          <Tooltip
            label="Clears cached GET data in this browser (for example categories), clears the last reply here, and pings the server. Use if answers felt stuck."
            hasArrow
          >
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Icon as={FiRefreshCw} />}
              onClick={handleClearVoiceAssistantCache}
              isLoading={portfolioVoiceCacheClearing}
              loadingText="Clearing"
              isDisabled={portfolioVoiceLoading}
            >
              Clear cache
            </Button>
          </Tooltip>
          {portfolioVoiceListening ? (
            <Text fontSize="xs" color="teal.600" maxW="md">
              Listening… use <strong>Cancel</strong> to stop without sending, or{" "}
              <strong>Done speaking</strong> to finish and send.
            </Text>
          ) : null}
        </HStack>
        {(portfolioVoiceListening || portfolioVoiceSpeaking) && (
          <HStack spacing={2} mt={2}>
            {portfolioVoiceListening ? (
              <Badge colorScheme="teal" variant="subtle">
                Listening
              </Badge>
            ) : null}
            {portfolioVoiceSpeaking ? (
              <Badge colorScheme="purple" variant="subtle">
                Speaking
              </Badge>
            ) : null}
          </HStack>
        )}
        {portfolioVoiceReply ? (
          <Box
            mt={3}
            p={3}
            borderRadius="md"
            bg={breadcrumbBg}
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Text fontSize="xs" fontWeight="semibold" color={headingColor} mb={1}>
              Assistant
            </Text>
            <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap">
              {portfolioVoiceReply}
            </Text>
            {portfolioVoiceSuggested.length > 0 ? (
              <Box mt={3}>
                <Text fontSize="xs" fontWeight="semibold" color={headingColor} mb={2}>
                  Top matches (say one, or first / second / third)
                </Text>
                <HStack spacing={2} flexWrap="wrap" align="stretch">
                  {portfolioVoiceSuggested.map((name, i) => (
                    <Badge
                      key={`${name}-${i}`}
                      colorScheme="teal"
                      variant="subtle"
                      px={2}
                      py={1}
                      fontSize="0.7rem"
                      maxW="100%"
                      whiteSpace="normal"
                      textAlign="left"
                    >
                      {i + 1}. {name}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            ) : null}
          </Box>
        ) : null}
              </CardBody>
            </Card>

            <Card
              borderRadius="xl"
              borderWidth="1px"
              borderColor={borderColor}
              bg={boxBgColor}
              boxShadow="sm"
              overflow="hidden"
            >
              <CardHeader
                py={4}
                borderBottomWidth="1px"
                borderColor={mutedLine}
                bg={locationStripBg}
              >
                <HStack justify="space-between" align="center" flexWrap="wrap" spacing={3}>
                  <HStack spacing={3} flexWrap="wrap" align="center">
                    <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="md">
                      Level {portfolioLevelLabel.level}
                    </Badge>
                    <Text
                      fontSize="sm"
                      color={subtleLabel}
                      textAlign="left"
                      maxW="3xl"
                      noOfLines={4}
                    >
                      {portfolioLevelLabel.pathLabel}
                    </Text>
                  </HStack>
                </HStack>
              </CardHeader>
              <CardBody p={{ base: 4, md: 5 }} textAlign="left">
          <Text fontWeight="semibold" fontSize="sm" color={headingColor} mb={1}>
            Search transactions
          </Text>
          <Text fontSize="xs" color="gray.500" mb={2}>
            {selectedCategory
              ? "Matches in this folder and nested folders."
              : "Matches across all portfolio folders."}{" "}
            {portfolioSearchScopeLabel
              ? `Server scope: ${portfolioSearchScopeLabel}`
              : ""}
          </Text>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Choose what to match: <strong>Description</strong> (transaction
            description, remark, details) and/or <strong>Folder name</strong>{" "}
            (category). Turn on at least one, then type your search.
          </Text>
          <HStack spacing={6} mb={3} flexWrap="wrap" align="center">
            <Text fontSize="xs" fontWeight="semibold" color={headingColor}>
              Search in:
            </Text>
            <Checkbox
              isChecked={portfolioSearchMatchDescription}
              onChange={(e) =>
                setPortfolioSearchMatchDescription(e.target.checked)
              }
              colorScheme="teal"
            >
              <Text fontSize="sm">Description</Text>
            </Checkbox>
            <Checkbox
              isChecked={portfolioSearchMatchCategory}
              onChange={(e) => setPortfolioSearchMatchCategory(e.target.checked)}
              colorScheme="teal"
            >
              <Text fontSize="sm">Folder name (category)</Text>
            </Checkbox>
          </HStack>
          <Flex
            gap={2}
            flexDir={{ base: "column", sm: "row" }}
            align={{ base: "stretch", sm: "flex-end" }}
            mb={portfolioSearchQuery.trim() ? 3 : 0}
          >
            <Input
              placeholder="Type search text…"
              value={portfolioSearchQuery}
              onChange={(e) => setPortfolioSearchQuery(e.target.value)}
              bg={inputBgColor}
              borderColor={borderColor}
              size="md"
              flex="1"
              minW={0}
              isDisabled={
                !portfolioSearchMatchDescription &&
                !portfolioSearchMatchCategory
              }
              _focus={{
                borderColor: inputBorderColor,
                boxShadow: `0 0 0 1px ${inputBorderColor}`,
              }}
            />
            <Button
              colorScheme="teal"
              size="md"
              onClick={() => runPortfolioSearch(1)}
              isLoading={portfolioSearchLoading}
              isDisabled={
                !portfolioSearchQuery.trim() ||
                (!portfolioSearchMatchDescription &&
                  !portfolioSearchMatchCategory)
              }
              flexShrink={0}
            >
              Search now
            </Button>
          </Flex>
          {portfolioSearchQuery.trim() &&
          (portfolioSearchMatchDescription || portfolioSearchMatchCategory) ? (
            <>
              {portfolioSearchLoading && portfolioSearchRows.length === 0 ? (
                <Flex justify="center" py={6}>
                  <Spinner size="md" color="teal.500" />
                </Flex>
              ) : null}
              {!portfolioSearchLoading &&
              portfolioSearchTotal === 0 &&
              portfolioSearchQuery.trim() ? (
                <Text fontSize="sm" color="gray.500">
                  No transactions matched your search in this scope.
                </Text>
              ) : null}
              {(portfolioSearchRows.length > 0 || portfolioSearchTotal > 0) && (
                <>
                  <TableContainer
                    overflowX="auto"
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={borderColor}
                  >
                    <Table size="sm" variant="simple">
                      <Thead bg={breadcrumbBg}>
                        <Tr>
                          <Th>Date</Th>
                          <Th>Folder</Th>
                          <Th>Matched fields</Th>
                          <Th>Description</Th>
                          <Th textAlign="center">Docs</Th>
                          <Th isNumeric>Amount</Th>
                          <Th isNumeric>Cash in</Th>
                          <Th isNumeric>Cash out</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {portfolioSearchRows.map((row) => {
                          const keys = Array.isArray(row.matchedKeys)
                            ? row.matchedKeys
                            : [];
                          const rowAttachmentUrls =
                            normalizePortfolioTransactionFileUrls(row.fileUrls);
                          const rowDesc =
                            row.desc || row.remark || row.details || "—";
                          return (
                            <Tr key={String(row._id)}>
                              <Td whiteSpace="nowrap" fontSize="xs">
                                {formatPortfolioSearchDateDisplay(row)}
                              </Td>
                              <Td fontSize="xs" maxW="140px">
                                <Text noOfLines={2}>
                                  {row.categoryName || "—"}
                                </Text>
                              </Td>
                              <Td>
                                <Flex gap={1} flexWrap="wrap" maxW="220px">
                                  {keys.map((k) => (
                                    <Badge
                                      key={k}
                                      fontSize="0.65rem"
                                      colorScheme="purple"
                                      variant="subtle"
                                    >
                                      {k}
                                    </Badge>
                                  ))}
                                </Flex>
                              </Td>
                              <Td fontSize="xs" maxW="200px">
                                <Text noOfLines={3}>{rowDesc}</Text>
                              </Td>
                              <Td textAlign="center" fontSize="xs">
                                {rowAttachmentUrls.length === 0 ? (
                                  "—"
                                ) : (
                                  <IconButton
                                    aria-label="View supporting documents"
                                    icon={<Icon as={FiPaperclip} />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="purple"
                                    onClick={() => {
                                      setTransactionAttachmentsModalUrls(
                                        rowAttachmentUrls
                                      );
                                      setTransactionAttachmentsModalTitle(
                                        `Documents — ${rowDesc !== "—" ? rowDesc : "Transaction"}`
                                      );
                                      transactionAttachmentsModal.onOpen();
                                    }}
                                  />
                                )}
                              </Td>
                              <Td isNumeric fontSize="xs">
                                {Number(row.amount ?? 0).toLocaleString("en-IN")}
                              </Td>
                              <Td isNumeric fontSize="xs">
                                {Number(row.cashIn ?? 0).toLocaleString("en-IN")}
                              </Td>
                              <Td isNumeric fontSize="xs">
                                {Number(row.cashOut ?? 0).toLocaleString(
                                  "en-IN"
                                )}
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  <Flex
                    justify="space-between"
                    align="center"
                    flexWrap="wrap"
                    gap={3}
                    mt={3}
                  >
                    <Text fontSize="xs" color="gray.500">
                      Page {portfolioSearchPage} of {portfolioSearchTotalPages}{" "}
                      · {portfolioSearchTotal} match
                      {portfolioSearchTotal !== 1 ? "es" : ""}
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() =>
                          runPortfolioSearch(Math.max(1, portfolioSearchPage - 1))
                        }
                        isDisabled={
                          portfolioSearchPage <= 1 || portfolioSearchLoading
                        }
                      >
                        Previous
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() =>
                          runPortfolioSearch(
                            Math.min(
                              portfolioSearchTotalPages,
                              portfolioSearchPage + 1
                            )
                          )
                        }
                        isDisabled={
                          portfolioSearchPage >= portfolioSearchTotalPages ||
                          portfolioSearchLoading
                        }
                      >
                        Next
                      </Button>
                    </HStack>
                  </Flex>
                </>
              )}
            </>
          ) : null}
              </CardBody>
            </Card>

            {categoryPath?.length > 0 && (
              <Card
                mb={2}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={breadcrumbBorder}
                overflow="hidden"
                boxShadow="sm"
                bg={boxBgColor}
              >
                <CardBody
                  px={{ base: 3, lg: 4 }}
                  py={3}
                  bg={breadcrumbBg}
                  textAlign="left"
                >
                  <HStack justifyContent="space-between" alignItems="center" mb={2}>
                    <Badge colorScheme="teal" px={2} py={1} borderRadius="md">
                      Current path
                    </Badge>
                    <Text fontSize="xs" color={subtleLabel}>
                      Click any step to jump back
                    </Text>
                  </HStack>
                  <Breadcrumb separator="›" fontWeight="semibold" fontSize="sm">
                    {categoryPath.map((category, index) => {
                      const isLastItem = index === categoryPath.length - 1;
                      return (
                        <BreadcrumbItem key={category._id} isCurrentPage={isLastItem}>
                          <BreadcrumbLink
                            onClick={() => handleCategorySelect(category, true, index)}
                            px={3}
                            py={1}
                            borderRadius="full"
                            bg={isLastItem ? breadcrumbCurrentBg : "transparent"}
                            color={
                              isLastItem ? breadcrumbCurrentColor : breadcrumbLinkColor
                            }
                            _hover={{
                              textDecoration: "none",
                              bg: isLastItem
                                ? breadcrumbCurrentBg
                                : breadcrumbLinkHoverBg,
                            }}
                          >
                            {category.name}
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                      );
                    })}
                  </Breadcrumb>
                </CardBody>
              </Card>
            )}

      {/* Show Categories or Subcategories based on selection */}
      {!selectedCategory ? (
        <VStack spacing={5} align="stretch">
          {categories.length > 0 && (
            <Box
              w="full"
              borderRadius="xl"
              borderWidth="1px"
              borderColor={borderColor}
              overflow="hidden"
              boxShadow="md"
              bg={boxBgColor}
            >
              <Box
                px={{ base: 4, md: 6 }}
                py={4}
                borderBottomWidth="1px"
                borderColor={borderColor}
                bg={breadcrumbBg}
              >
                <Heading
                  size="sm"
                  textAlign="left"
                  color={headingColor}
                  fontWeight="bold"
                >
                  Your categories
                </Heading>
                <Text
                  fontSize="sm"
                  color="gray.500"
                  mt={1}
                  textAlign="left"
                >
                  {categories.length} folder
                  {categories.length !== 1 ? "s" : ""} — open one to add
                  transactions or subfolders
                </Text>
                <Text fontSize="xs" color="gray.500" mt={2} textAlign="left">
                  Drag the move handle on a card, then drop it on another card to
                  change order. Order is saved to your account.
                </Text>
                {portfolioRootOrderSaving ? (
                  <Flex mt={2} align="center" gap={2}>
                    <Spinner size="xs" color="teal.500" />
                    <Text fontSize="xs" color="gray.500">
                      Saving order…
                    </Text>
                  </Flex>
                ) : null}
              </Box>
              <Box p={{ base: 4, md: 5 }} bg={categorySectionBodyBg}>
                <SimpleGrid
                  columns={{ base: 1, md: 2, xl: 3 }}
                  spacing={4}
                  alignItems="stretch"
                >
                  {categories.map((category) => (
                    <Box
                      key={category._id}
                      opacity={
                        draggingPortfolioRootId === String(category._id)
                          ? 0.55
                          : 1
                      }
                      transition="opacity 0.15s ease"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(e) => {
                        onPortfolioRootFolderDrop(e, category._id);
                      }}
                    >
                      <PortfolioCategoryFolderCard
                        category={category}
                        variant="teal"
                        collaborationShared={!!category.collaborationShared}
                        collaborationCanInvite={!!category.collaborationCanInvite}
                        onShareClick={
                          !category.collaborationShared || category.collaborationCanInvite
                            ? () => {
                                setSharePortfolioCategory(category);
                                sharePortfolioModal.onOpen();
                              }
                            : undefined
                        }
                        isEditingThisRow={
                          isEditing?.id === category._id &&
                          isEditing.type === "category"
                        }
                        editedCategoryName={editedCategoryName}
                        onEditedCategoryNameChange={setEditedCategoryName}
                        onSelect={() => handleCategorySelect(category)}
                        onSaveEdit={() =>
                          handleCategoryUpdate(category._id, editedCategoryName)
                        }
                        onStartEdit={() =>
                          handleStartEdit(category, "category")
                        }
                        onDelete={handleCategoryDelete}
                        deleteAriaLabel="Delete Category"
                        saveAriaLabel="Save category name"
                        editAriaLabel="Edit category"
                        borderColor={borderColor}
                        textColor={textColor}
                        inputBgColor={inputBgColor}
                        amountColor={amountColor}
                        categoryRowBg={categoryRowBg}
                        categoryRowHoverBg={categoryRowHoverBg}
                        categoryIconBg={categoryIconBgRoot}
                        onToggleHideAmountSum={(checked) =>
                          handlePortfolioHideAmountSumToggle(
                            category._id,
                            checked
                          )
                        }
                        hideAmountSumSaving={
                          hideAmountSavingId === String(category._id)
                        }
                        dragHandleSlot={
                          <IconButton
                            icon={<Icon as={FiMove} />}
                            aria-label="Drag to reorder top-level folders"
                            title="Drag to reorder — order is saved for your account"
                            size="sm"
                            variant="ghost"
                            colorScheme="gray"
                            cursor="grab"
                            _active={{ cursor: "grabbing" }}
                            draggable
                            onClick={(e) => e.stopPropagation()}
                            onDragStart={(e) => {
                              e.stopPropagation();
                              onPortfolioRootDragStart(category._id);
                              e.dataTransfer.setData(
                                "text/plain",
                                String(category._id)
                              );
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onDragEnd={() => setDraggingPortfolioRootId(null)}
                          />
                        }
                      />
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            </Box>
          )}

          {/* Add New Category */}
          <Box
            mt={6}
            p={4}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            bg={boxBgColor}
            boxShadow="sm"
            borderLeftWidth="4px"
            borderLeftColor="teal.500"
            w="full"
            maxW={{ lg: "720px" }}
            mx="auto"
          >
            <VStack align="stretch" spacing={3}>
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={headingColor}
                textAlign="left"
              >
                New top-level folder
              </Text>
              <Text fontSize="xs" color="gray.500" textAlign="left">
                Adds a folder at the portfolio root. Press Enter when done.
              </Text>
              <Flex
                gap={3}
                direction={{ base: "column", sm: "row" }}
                align={{ base: "stretch", sm: "flex-end" }}
              >
                <Input
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddCategory();
                  }}
                  placeholder="Folder name"
                  size="md"
                  bg={inputBgColor}
                  borderRadius="md"
                  flex="1"
                  minW={0}
                  borderColor={borderColor}
                  _focus={{
                    borderColor: inputBorderColor,
                    boxShadow: `0 0 0 1px ${inputBorderColor}`,
                  }}
                />
                <Button
                  onClick={handleAddCategory}
                  colorScheme={buttonColorScheme}
                  size="md"
                  px={6}
                  minW={{ sm: "132px" }}
                >
                  Create folder
                </Button>
              </Flex>
            </VStack>
          </Box>

          <VStack spacing={2} align="center" mt={10} mb={2} alignSelf="center">
            <Button
              colorScheme="purple"
              onClick={openImportModal}
              whiteSpace="normal"
              textAlign="center"
              h="auto"
              py={3}
              px={5}
              maxW="md"
              lineHeight="short"
              aria-label="Import data from Cashbook app CSV export into a new parent category"
            >
              Import from Cashbook (CSV file)
            </Button>
          </VStack>
        </VStack>
      ) : (
        <>
          <Flex
            justify="space-between"
            align="flex-start"
            gap={4}
            mb={6}
            mt={{ base: 4, md: 6 }}
            flexWrap="wrap"
          >
            <Box flex="1" minW={0} textAlign="left">
              <Heading
                as="h2"
                size="lg"
                color={headingColor}
                fontWeight="bold"
                lineHeight="short"
                noOfLines={3}
              >
                {selectedCategory.name}
              </Heading>
              {selectedCategory.portfolioHideAmountSum ? (
                <Text
                  fontSize="sm"
                  color="gray.500"
                  mt={2}
                  fontStyle="italic"
                >
                  Total balance hidden for this folder
                </Text>
              ) : (
                <Box mt={3} maxW="sm">
                  <Text
                    fontSize="10px"
                    fontWeight="semibold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                    mb={1}
                  >
                    Total balance
                  </Text>
                  <HStack spacing={1.5} align="baseline" flexWrap="wrap">
                    <Text
                      as="span"
                      fontSize="md"
                      fontWeight="bold"
                      color="gray.500"
                      fontVariantNumeric="tabular-nums"
                    >
                      ₹
                    </Text>
                    <Text
                      as="span"
                      fontSize={{ base: "2xl", md: "3xl" }}
                      fontWeight="extrabold"
                      fontVariantNumeric="tabular-nums"
                      letterSpacing="-0.03em"
                      lineHeight="1.1"
                      color={
                        Number(selectedCategory.totalSum) < 0
                          ? folderHeaderNegativeTotalColor
                          : amountColor
                      }
                      title={formatINRPortfolioValue(selectedCategory.totalSum)}
                    >
                      {formatINRPortfolioValue(selectedCategory.totalSum)}
                    </Text>
                  </HStack>
                </Box>
              )}
              <Text fontSize="sm" color="gray.500" mt={2} maxW="lg">
                Open a form below when you need it. Use the path above to move
                to a parent folder.
              </Text>
            </Box>
            <IconButton
              icon={<CloseIcon />}
              size="lg"
              colorScheme="red"
              flexShrink={0}
              onClick={handleCloseCategoryView}
              aria-label={
                categoryPath.length > 1
                  ? "Back to parent folder"
                  : "Back to all categories"
              }
            />
          </Flex>

          <Box
            mb={6}
            p={{ base: 3, md: 4 }}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            bg={inputBgColor}
            boxShadow="sm"
            maxW="2xl"
          >
            <HStack justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
              <Text fontSize="sm" fontWeight="semibold" color={headingColor}>
                Quick actions
              </Text>
              <Badge colorScheme="teal" borderRadius="full" px={2.5} py={0.5}>
                One at a time
              </Badge>
            </HStack>
            <HStack spacing={2} flexWrap="wrap">
              <Button
                size="sm"
                borderRadius="full"
                colorScheme={activeFolderTool === "transaction" ? "teal" : "gray"}
                variant={activeFolderTool === "transaction" ? "solid" : "outline"}
                onClick={() => setActiveFolderTool("transaction")}
              >
                Record transaction
              </Button>
              <Button
                size="sm"
                borderRadius="full"
                colorScheme={activeFolderTool === "subfolder" ? "teal" : "gray"}
                variant={activeFolderTool === "subfolder" ? "solid" : "outline"}
                onClick={() => setActiveFolderTool("subfolder")}
              >
                Add subfolder
              </Button>
              <Button
                size="sm"
                borderRadius="full"
                colorScheme={activeFolderTool === "pdf" ? "teal" : "gray"}
                variant={activeFolderTool === "pdf" ? "solid" : "outline"}
                onClick={() => setActiveFolderTool("pdf")}
              >
                PDF report
              </Button>
              <Button
                size="sm"
                borderRadius="full"
                colorScheme={activeFolderTool == null ? "red" : "gray"}
                variant={activeFolderTool == null ? "solid" : "outline"}
                onClick={() => setActiveFolderTool(null)}
              >
                Hide forms
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={3}>
              Choose any action to open its form. Tap "Hide forms" to keep this section clean.
            </Text>
          </Box>

          <Box mb={6} textAlign="left">
            <Button
              leftIcon={<Icon as={FiTrendingUp} />}
              colorScheme="teal"
              variant="solid"
              size="md"
              borderRadius="lg"
              onClick={() => setIsFlowCategoryBalanceOpen(true)}
            >
              Show analytics — flow &amp; category balance
            </Button>
            <Text fontSize="xs" color="gray.500" mt={2} maxW="lg">
              Opens a crisp full-screen panel with cash-flow and subfolder totals.
            </Text>
          </Box>

          <Modal
            isOpen={isFlowCategoryBalanceOpen}
            onClose={() => setIsFlowCategoryBalanceOpen(false)}
            size="2xl"
            motionPreset="scale"
            scrollBehavior="inside"
            closeOnOverlayClick={false}
            closeOnEsc
            blockScrollOnMount
            isCentered
          >
            <ModalOverlay bg="blackAlpha.500" />
            <ModalContent
              mx={{ base: 3, md: 6 }}
              borderRadius="2xl"
              display="flex"
              flexDirection="column"
              boxShadow="2xl"
              bg={boxBgColor}
              borderWidth="1px"
              borderColor={borderColor}
              maxW="min(92vw, 980px)"
            >
              <ModalHeader
                borderBottomWidth="1px"
                borderColor={borderColor}
                py={4}
                px={{ base: 4, md: 6 }}
                flexShrink={0}
                bg={breadcrumbBg}
                position="relative"
                zIndex={2}
                pointerEvents="auto"
              >
                <Flex align="flex-start" justify="space-between" gap={4} flexWrap="wrap">
                  <Box>
                    <Heading size="md" color={headingColor}>
                      Flow &amp; category balance
                    </Heading>
                    <Text fontSize="sm" color="gray.500" mt={2} fontWeight="normal">
                      IST days • {portfolioAnalyticsRangeLabel.toLowerCase()} • subtree under
                      &quot;{selectedCategory?.name || "folder"}&quot;.
                      Subfolder bars: direct children only.
                    </Text>
                    <HStack mt={3} spacing={2} flexWrap="wrap">
                      {PORTFOLIO_ANALYTICS_RANGES.map((range) => (
                        <Button
                          key={range.id}
                          size="sm"
                          variant={
                            portfolioAnalyticsRange === range.id ? "solid" : "outline"
                          }
                          colorScheme="teal"
                          borderRadius="full"
                          onClick={() => setPortfolioAnalyticsRange(range.id)}
                        >
                          {range.label}
                        </Button>
                      ))}
                    </HStack>
                    <HStack mt={3} spacing={2} flexWrap="wrap" align="flex-end">
                      <FormControl maxW={{ base: "100%", sm: "200px" }} w="full">
                        <FormLabel fontSize="xs" color="gray.500" mb={1}>
                          Hide chart amounts above (₹)
                        </FormLabel>
                        <Input
                          size="sm"
                          type="text"
                          inputMode="decimal"
                          placeholder="Off — full range"
                          value={portfolioFlowExcludeAboveInput}
                          onChange={(e) =>
                            setPortfolioFlowExcludeAboveInput(e.target.value)
                          }
                          bg={inputBgColor}
                          aria-describedby="flow-exclude-hint"
                        />
                      </FormControl>
                      <HStack spacing={1} flexWrap="wrap">
                        {[25000, 50000, 100000].map((preset) => (
                          <Button
                            key={preset}
                            size="xs"
                            variant={
                              portfolioFlowExcludeAboveParsedInr === preset
                                ? "solid"
                                : "outline"
                            }
                            colorScheme="teal"
                            borderRadius="md"
                            onClick={() =>
                              setPortfolioFlowExcludeAboveInput(String(preset))
                            }
                          >
                            {preset >= 100000
                              ? "1 lakh"
                              : `${preset / 1000}k`}
                          </Button>
                        ))}
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setPortfolioFlowExcludeAboveInput("")}
                        >
                          Clear
                        </Button>
                      </HStack>
                    </HStack>
                    <Text
                      id="flow-exclude-hint"
                      fontSize="xs"
                      color="gray.500"
                      mt={1}
                    >
                      Per time bucket &amp; per bar total: sides over this amount
                      are omitted so smaller movement stays readable.
                    </Text>
                    {portfolioAnalyticsRange === "custom" ? (
                      <HStack mt={3} spacing={3} flexWrap="wrap" align="flex-end">
                        <FormControl maxW="180px">
                          <FormLabel fontSize="xs" color="gray.500">
                            From
                          </FormLabel>
                          <Input
                            type="date"
                            size="sm"
                            value={portfolioAnalyticsCustomStart}
                            onChange={(e) =>
                              setPortfolioAnalyticsCustomStart(e.target.value)
                            }
                            bg={inputBgColor}
                          />
                        </FormControl>
                        <FormControl maxW="180px">
                          <FormLabel fontSize="xs" color="gray.500">
                            To
                          </FormLabel>
                          <Input
                            type="date"
                            size="sm"
                            value={portfolioAnalyticsCustomEnd}
                            onChange={(e) =>
                              setPortfolioAnalyticsCustomEnd(e.target.value)
                            }
                            bg={inputBgColor}
                          />
                        </FormControl>
                        <Button
                          size="sm"
                          colorScheme="teal"
                          onClick={() => setPortfolioAnalyticsRange("custom")}
                          isDisabled={
                            !portfolioAnalyticsCustomStart ||
                            !portfolioAnalyticsCustomEnd
                          }
                          alignSelf="flex-end"
                        >
                          Apply
                        </Button>
                      </HStack>
                    ) : null}
                    <HStack mt={3} spacing={3} align="center" flexWrap="wrap">
                      {portfolioFolderVizBusy ? <Spinner size="sm" /> : null}
                      {portfolioDailyViz?.truncated ? (
                        <Badge colorScheme="orange" borderRadius="md">
                          Sample capped — many transactions loaded
                        </Badge>
                      ) : null}
                      {portfolioFlowExcludeAboveParsedInr > 0 ? (
                        <Badge colorScheme="cyan" borderRadius="md">
                          Hiding &gt; ₹{portfolioFlowExcludeAboveParsedInr.toLocaleString("en-IN")}
                        </Badge>
                      ) : null}
                    </HStack>
                  </Box>
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="solid"
                    onClick={() => setIsFlowCategoryBalanceOpen(false)}
                  >
                    Close
                  </Button>
                </Flex>
              </ModalHeader>
              <ModalBody
                flex="1 1 auto"
                minH={0}
                minW={0}
                display="flex"
                flexDirection="column"
                overflowY="auto"
                px={{ base: 4, md: 6 }}
                py={5}
              >
                  <Flex
                    direction="column"
                    gap={portfolioFlowModalHasBars ? 4 : 0}
                  >
                  <Box
                    w="full"
                    position="relative"
                    minH={{
                      base: `${chartHeightForPoints(
                        portfolioFlowModalSeries.length,
                        360
                      )}px`,
                      md: `${chartHeightForPoints(
                        portfolioFlowModalSeries.length,
                        420
                      )}px`,
                    }}
                    overflow="hidden"
                  >
                    {portfolioFlowModalLoadingSeries ? (
                      <Flex
                        align="center"
                        justify="center"
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                      >
                        <Spinner color="teal.500" size="lg" />
                      </Flex>
                    ) : portfolioFlowModalHasDailySeries ? (
                      <Box w="full">
                        <PortfolioModalChartMeasure
                          active={isFlowCategoryBalanceOpen}
                        >
                          {({ w }) => (
                            <Box w="100%" overflowX="auto" overflowY="visible" py={3}>
                              <Box
                                w={chartWidthForPoints(
                                  portfolioFlowModalSeries.length,
                                  w
                                )}
                                h={portfolioFlowChartHeight}
                              >
                                <AreaChart
                                  width={chartWidthForPoints(
                                    portfolioFlowModalSeries.length,
                                    w
                                  )}
                                  height={portfolioFlowChartHeight}
                                  data={portfolioFlowModalSeriesForChart}
                                  margin={{ top: 32, right: 24, left: 12, bottom: 28 }}
                                  style={{ cursor: "crosshair", pointerEvents: "auto" }}
                                  onMouseMove={(state) => {
                                    const payload = state?.activePayload;
                                    const datum = payload?.[0]?.payload || null;
                                    setFlowHoverDatum(datum || null);
                                  }}
                                  onMouseLeave={() => setFlowHoverDatum(null)}
                                >
                              <defs>
                                <linearGradient id="portfolioCashInGradFs" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#059669" stopOpacity={0.8} />
                                  <stop offset="100%" stopColor="#059669" stopOpacity={0.05} />
                                </linearGradient>
                                <linearGradient id="portfolioCashOutGradFs" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#dc2626" stopOpacity={0.8} />
                                  <stop offset="100%" stopColor="#dc2626" stopOpacity={0.06} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="4 8" stroke={chartGridColor} />
                              <XAxis
                                dataKey="day"
                                tick={{ fontSize: 11, fill: chartAxisColor }}
                                tickFormatter={(d) =>
                                  typeof d === "string" ? d.slice(5) : d
                                }
                                interval={chartXAxisIntervalByLength(
                                  portfolioFlowModalSeries.length
                                )}
                                minTickGap={14}
                              />
                            <YAxis
                              tick={{ fontSize: 12, fill: chartAxisColor }}
                              tickFormatter={(v) => formatInr(Math.abs(v))}
                              domain={[-portfolioFlowAxisMax, portfolioFlowAxisMax]}
                              allowDataOverflow
                            />
                            <RechartsTooltip
                              content={<PortfolioFlowTooltip onShowInfo={openFlowBucketInfo} />}
                              wrapperStyle={{
                                zIndex: 2147483647,
                                pointerEvents: "auto",
                              }}
                              allowEscapeViewBox={{ x: true, y: true }}
                              cursor={{
                                stroke: chartAxisColor,
                                strokeWidth: 1,
                                strokeDasharray: "4 6",
                                opacity: 0.5,
                              }}
                            />
                              <Legend />
                              <Area
                                type="monotone"
                                dataKey="cashIn"
                                name="Cash in"
                                stroke="#059669"
                                strokeWidth={2}
                                fill="url(#portfolioCashInGradFs)"
                                isAnimationActive={false}
                                activeDot={{ r: 5 }}
                              />
                              <Area
                                type="monotone"
                                dataKey="cashOut"
                                name="Cash out"
                                stroke="#dc2626"
                                strokeWidth={2}
                                fill="url(#portfolioCashOutGradFs)"
                                isAnimationActive={false}
                                activeDot={{ r: 5 }}
                              />
                                </AreaChart>
                              </Box>
                            </Box>
                          )}
                        </PortfolioModalChartMeasure>
                        {portfolioFlowChartAllExcludedByThreshold ? (
                          <Text
                            fontSize="xs"
                            color="orange.500"
                            mt={2}
                            textAlign="center"
                          >
                            Every bucket in this range exceeds your threshold on
                            at least one side — lower the cutoff or clear it to see
                            the full chart again.
                          </Text>
                        ) : null}
                      </Box>
                    ) : portfolioFlowModalEmptyGlobally ? (
                      <Flex
                        align="center"
                        justify="center"
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        px={4}
                      >
                        <Text fontSize="md" color="gray.500" textAlign="center">
                          No movement in this range yet — flow will appear once you post entries.
                        </Text>
                      </Flex>
                    ) : (
                      <Flex
                        align="center"
                        justify="center"
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        px={4}
                      >
                        <Text fontSize="sm" color="gray.500" textAlign="center">
                          No daily cash-flow points in this range; subfolder totals are below.
                        </Text>
                      </Flex>
                    )}
                  </Box>

                  {portfolioFlowModalHasBars ? (
                    <Box w="full">
                      <PortfolioModalChartMeasure
                        active={
                          isFlowCategoryBalanceOpen &&
                          portfolioFlowModalHasBars
                        }
                      >
                        {({ w }) => (
                          <Box w="100%" overflowX="auto" overflowY="visible" py={3}>
                            <Box w={w} h={portfolioBarsChartHeight}>
                              <BarChart
                                width={w}
                                height={portfolioBarsChartHeight}
                                layout="vertical"
                                data={portfolioChildrenVizBarsData}
                                margin={{ top: 28, right: 28, left: 12, bottom: 28 }}
                                style={{ cursor: "pointer", pointerEvents: "auto" }}
                              >
                              <CartesianGrid
                                strokeDasharray="4 8"
                                horizontal={false}
                                stroke={chartGridColor}
                              />
                              <XAxis
                                type="number"
                                tick={{ fontSize: 12, fill: chartAxisColor }}
                              />
                              <YAxis
                                type="category"
                                dataKey="label"
                                width={180}
                                tick={{ fontSize: 12, fill: chartAxisColor }}
                              />
                            <RechartsTooltip
                              content={<PortfolioBarsTooltip />}
                              wrapperStyle={{
                                zIndex: 2147483647,
                                pointerEvents: "none",
                              }}
                              allowEscapeViewBox={{ x: true, y: true }}
                              cursor={{
                                fill: "rgba(148, 163, 184, 0.18)",
                              }}
                            />
                              <Legend />
                              <Bar
                                dataKey="cashIn"
                                name="Cash in"
                                fill="#059669"
                                radius={[0, 6, 6, 0]}
                                isAnimationActive={false}
                              />
                              <Bar
                                dataKey="cashOut"
                                name="Cash out"
                                fill="#dc2626"
                                radius={[0, 6, 6, 0]}
                                isAnimationActive={false}
                              />
                              </BarChart>
                            </Box>
                          </Box>
                        )}
                      </PortfolioModalChartMeasure>
                    </Box>
                  ) : null}
                </Flex>
              </ModalBody>
              <ModalFooter
                py={3}
                borderTopWidth="1px"
                borderColor={borderColor}
                flexShrink={0}
                bg={breadcrumbBg}
              >
                <Text fontSize="xs" color="gray.500">
                  Tip: press Esc to close.
                </Text>
              </ModalFooter>
            </ModalContent>
          </Modal>

          <Modal
            isOpen={flowBucketInfoOpen}
            onClose={closeFlowBucketInfo}
            size="4xl"
            motionPreset="scale"
            scrollBehavior="inside"
            isCentered
          >
            <ModalOverlay bg="blackAlpha.500" />
            <ModalContent
              mx={{ base: 3, md: 6 }}
              borderRadius="2xl"
              bg={boxBgColor}
              borderWidth="1px"
              borderColor={borderColor}
              maxW="min(92vw, 1100px)"
            >
              <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>
                <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
                  <Box>
                    <Heading size="sm" color={headingColor}>
                      {flowBucketInfoMode === "cashIn" ? "Cash in" : "Cash out"}{" "}
                      activity
                    </Heading>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {flowBucketInfoRange.start
                        ? flowBucketInfoRange.start === flowBucketInfoRange.end
                          ? flowBucketInfoRange.start
                          : `${flowBucketInfoRange.start} → ${flowBucketInfoRange.end}`
                        : "Selected range"}
                    </Text>
                  </Box>
                  <Button size="sm" colorScheme="red" onClick={closeFlowBucketInfo}>
                    Close
                  </Button>
                </Flex>
              </ModalHeader>
              <ModalBody px={{ base: 4, md: 6 }} py={5}>
                {flowBucketInfoLoading ? (
                  <Flex justify="center" py={10}>
                    <Spinner color="teal.500" />
                  </Flex>
                ) : (
                  <>
                    <HStack spacing={3} mb={3} flexWrap="wrap">
                      {flowBucketInfoTruncated ? (
                        <Badge colorScheme="orange" borderRadius="md">
                          Sample capped — too many rows
                        </Badge>
                      ) : null}
                      <Badge
                        colorScheme={flowBucketInfoMode === "cashIn" ? "green" : "red"}
                        borderRadius="md"
                      >
                        {flowBucketInfoRows.length} row
                        {flowBucketInfoRows.length === 1 ? "" : "s"}
                      </Badge>
                    </HStack>
                    {flowBucketInfoRows.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">
                        No{" "}
                        {flowBucketInfoMode === "cashIn" ? "cash in" : "cash out"}{" "}
                        entries in this bucket.
                      </Text>
                    ) : (
                      <TableContainer
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor={borderColor}
                        maxH={{ base: "none", md: "min(70vh, 520px)" }}
                        overflowY={{ base: "visible", md: "auto" }}
                        overflowX="auto"
                      >
                        <Table size="sm" variant="simple">
                          <Thead bg={breadcrumbBg} position="sticky" top={0} zIndex={1}>
                            <Tr>
                              <Th>Date</Th>
                              <Th>Folder</Th>
                              <Th>Description</Th>
                              <Th isNumeric>Cash in</Th>
                              <Th isNumeric>Cash out</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {flowBucketInfoRows.map((row, idx) => (
                              <Tr key={`${row.folder}-${row.date}-${idx}`}>
                                <Td fontSize="xs" whiteSpace="nowrap">
                                  {row.date || "—"}
                                </Td>
                                <Td fontSize="xs">
                                  <Text noOfLines={2}>{row.folder || "—"}</Text>
                                </Td>
                                <Td fontSize="xs" maxW="240px">
                                  <Text noOfLines={2}>
                                    {row.description || "—"}
                                  </Text>
                                </Td>
                                <Td isNumeric fontSize="xs">
                                  {formatInr(row.cashIn)}
                                </Td>
                                <Td isNumeric fontSize="xs">
                                  {formatInr(row.cashOut)}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>

          {(activeFolderTool === "transaction" ||
            activeFolderTool === "subfolder") && (
          <Flex
            mb={10}
            gap={6}
            justify="center"
            flexDirection={{ base: "column", lg: "row" }}
            width="100%"
          >
            {activeFolderTool === "transaction" && (
            <Box
              bg={boxBgColor}
              p={6}
              borderRadius="lg"
              boxShadow="2xl"
              width={{
                base: "100%",
                lg: activeFolderTool === "subfolder" ? "45%" : "100%",
              }}
              maxW={activeFolderTool === "subfolder" ? undefined : "720px"}
              mx={activeFolderTool === "subfolder" ? undefined : "auto"}
              transition="all 0.3s ease"
            >
              <VStack spacing={4} align="stretch">
                <Heading size="md" color={headingColor} textAlign="center">
                  New transaction
                </Heading>
                <FormControl>
                  <FormLabel textAlign="left">Post to folder</FormLabel>
                  <Popover
                    matchWidth
                    placement="bottom-start"
                    isOpen={transactionCategoryPicker.isOpen}
                    onClose={() => {
                      transactionCategoryPicker.onClose();
                      setTransactionCategorySearch("");
                    }}
                    closeOnBlur
                  >
                    <PopoverTrigger>
                      <Button
                        w="100%"
                        size="lg"
                        variant="outline"
                        justifyContent="space-between"
                        fontWeight="normal"
                        bg={inputBgColor}
                        borderColor={borderColor}
                        rightIcon={<ChevronDownIcon />}
                        _hover={{ bg: categoryRowHoverBg }}
                        _active={{ bg: categoryRowHoverBg }}
                        onClick={transactionCategoryPicker.onToggle}
                        maxW="100%"
                      >
                        <Text
                          as="span"
                          noOfLines={1}
                          textAlign="left"
                          flex="1"
                          pr={2}
                        >
                          {selectedTransactionCategoryLabel}
                        </Text>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      borderColor={borderColor}
                      boxShadow="lg"
                      p={0}
                    >
                      <PopoverBody p={3}>
                        <Input
                          ref={transactionCategorySearchRef}
                          size="sm"
                          placeholder="Search folders…"
                          value={transactionCategorySearch}
                          onChange={(e) =>
                            setTransactionCategorySearch(e.target.value)
                          }
                          mb={2}
                          bg={inputBgColor}
                          borderColor={borderColor}
                          _focus={{
                            borderColor: focusBorderColor,
                            boxShadow: `0 0 0 1px ${focusBorderColor}`,
                          }}
                        />
                        <Divider mb={2} borderColor={borderColor} />
                        <VStack
                          align="stretch"
                          spacing={0}
                          maxH="240px"
                          overflowY="auto"
                        >
                          {filteredTransactionCategoryOptions.length === 0 ? (
                            <Text
                              fontSize="sm"
                              color="gray.500"
                              py={2}
                              textAlign="center"
                            >
                              No folders match
                            </Text>
                          ) : (
                            filteredTransactionCategoryOptions.map((opt) => (
                              <Button
                                key={opt.id}
                                variant="ghost"
                                size="sm"
                                h="auto"
                                py={2}
                                justifyContent="flex-start"
                                whiteSpace="normal"
                                fontWeight={
                                  opt.id === transactionCategoryId
                                    ? "semibold"
                                    : "normal"
                                }
                                bg={
                                  opt.id === transactionCategoryId
                                    ? catOptionSelectedBg
                                    : "transparent"
                                }
                                onClick={() => {
                                  setTransactionCategoryId(opt.id);
                                  transactionCategoryPicker.onClose();
                                  setTransactionCategorySearch("");
                                }}
                                _hover={{
                                  bg:
                                    opt.id === transactionCategoryId
                                      ? catOptionSelectedBg
                                      : catOptionHoverBg,
                                }}
                              >
                                {opt.label}
                              </Button>
                            ))
                          )}
                        </VStack>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </FormControl>
                <Input
                  value={transactionName}
                  onChange={(e) => setTransactionName(e.target.value)}
                  placeholder="Short description"
                  size="lg"
                  bg={inputBgColor}
                  borderRadius="md"
                  boxShadow="md"
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px blue.500",
                  }}
                />
                <FormControl>
                  <FormLabel textAlign="left" mb={2}>
                    Money in or out
                  </FormLabel>
                  <RadioGroup
                    value={newTransactionCashFlow}
                    onChange={setNewTransactionCashFlow}
                  >
                    <HStack spacing={6} flexWrap="wrap" align="flex-start">
                      <Radio value="out">Paid out (spent)</Radio>
                      <Radio value="in">Received (income)</Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>
                <Input
                  type="number"
                  value={transactionAmount}
                  onChange={(e) => setTransactionAmount(e.target.value)}
                  placeholder="Amount (₹)"
                  size="lg"
                  bg={inputBgColor}
                  borderRadius="md"
                  boxShadow="md"
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px blue.500",
                  }}
                />
                <FormControl textAlign="left">
                  <FormLabel mb={2} fontSize="sm" fontWeight="semibold">
                    Supporting documents (optional)
                  </FormLabel>
                  <input
                    ref={supportingTransactionDocsInputRef}
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const picked = Array.from(e.target.files || []);
                      setPendingTransactionSupportingFiles((prev) =>
                        [...prev, ...picked].slice(
                          0,
                          MAX_PORTFOLIO_SUPPORTING_DOC_FILES
                        )
                      );
                      e.target.value = "";
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    leftIcon={<Icon as={FiPaperclip} />}
                    onClick={() =>
                      supportingTransactionDocsInputRef.current?.click()
                    }
                    isDisabled={
                      pendingTransactionSupportingFiles.length >=
                      MAX_PORTFOLIO_SUPPORTING_DOC_FILES
                    }
                  >
                    Add files
                  </Button>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Up to {MAX_PORTFOLIO_SUPPORTING_DOC_FILES} files. Uploaded
                    when you save.
                  </Text>
                  {pendingTransactionSupportingFiles.length > 0 ? (
                    <VStack align="stretch" spacing={1} mt={2}>
                      {pendingTransactionSupportingFiles.map((f, idx) => (
                        <HStack
                          key={`${f.name}-${f.size}-${idx}`}
                          justify="space-between"
                          bg={inputBgColor}
                          borderRadius="md"
                          px={2}
                          py={1}
                          borderWidth="1px"
                          borderColor={borderColor}
                        >
                          <Text fontSize="xs" noOfLines={1} flex="1">
                            {f.name}
                          </Text>
                          <IconButton
                            aria-label="Remove file"
                            icon={<CloseIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={() =>
                              setPendingTransactionSupportingFiles((prev) =>
                                prev.filter((_, i) => i !== idx)
                              )
                            }
                          />
                        </HStack>
                      ))}
                    </VStack>
                  ) : null}
                </FormControl>
                <Button
                  onClick={handleAddTransaction}
                  colorScheme="blue"
                  size="lg"
                  boxShadow="lg"
                  isLoading={transactionSupportingDocsBusy}
                  isDisabled={transactionSupportingDocsBusy}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  Save transaction
                </Button>
              </VStack>
            </Box>
            )}

            {activeFolderTool === "subfolder" && (
            <Box
              bg={boxBgColor}
              p={4}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="sm"
              borderLeftWidth="4px"
              borderLeftColor="cyan.500"
              width={{
                base: "100%",
                lg: activeFolderTool === "transaction" ? "45%" : "100%",
              }}
              maxW={activeFolderTool === "transaction" ? undefined : "720px"}
              mx={activeFolderTool === "transaction" ? undefined : "auto"}
            >
              <VStack spacing={3} align="stretch">
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color={headingColor}
                  textAlign="left"
                >
                  New subfolder
                </Text>
                <Text fontSize="xs" color="gray.500" textAlign="left">
                  Goes inside “{selectedCategory.name}”. Press Enter to save.
                </Text>
                <Flex
                  gap={3}
                  direction={{ base: "column", sm: "row" }}
                  align={{ base: "stretch", sm: "flex-end" }}
                >
                  <Input
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddCategory();
                    }}
                    placeholder="Name of the new subfolder"
                    size="md"
                    bg={inputBgColor}
                    borderRadius="md"
                    flex="1"
                    minW={0}
                    borderColor={borderColor}
                    _focus={{
                      borderColor: focusBorderColor,
                      boxShadow: `0 0 0 1px ${focusBorderColor}`,
                    }}
                  />
                  <Button
                    onClick={handleAddCategory}
                    colorScheme="teal"
                    size="md"
                    px={6}
                    minW={{ sm: "140px" }}
                  >
                    Create subfolder
                  </Button>
                </Flex>
              </VStack>
            </Box>
            )}
          </Flex>
          )}

          {activeFolderTool === "pdf" && (
          <Box
            w="100%"
            mt={2}
            mb={6}
            p={{ base: 4, md: 5 }}
            borderRadius="xl"
            borderWidth="1px"
            borderColor={borderColor}
            bg={boxBgColor}
            boxShadow="md"
            borderTopWidth="4px"
            borderTopColor="purple.400"
          >
            <Flex
              align="flex-start"
              justify="space-between"
              gap={3}
              mb={3}
              flexWrap="wrap"
            >
              <Box minW={0}>
                <Heading size="sm" color={headingColor} textAlign="left">
                  PDF report
                </Heading>
                <Text fontSize="xs" color="gray.500" mt={1} textAlign="left">
                  Choose folders, pick totals or every row, then download a PDF
                  with proper tables on your device.
                </Text>
              </Box>
              {analyticsLoadingSubtree ? <Spinner size="sm" /> : null}
            </Flex>

            {savedPortfolioReports.length > 0 && (
              <Box
                mb={4}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                borderColor={borderColor}
                bg={breadcrumbBg}
              >
                <Text fontWeight="semibold" mb={2} fontSize="sm" textAlign="left">
                  Saved reports (this folder, this browser)
                </Text>
                <VStack align="stretch" spacing={2}>
                  {savedPortfolioReports.map((r) => (
                    <Flex
                      key={r.id}
                      flexWrap="wrap"
                      gap={2}
                      justify="space-between"
                      align="center"
                      py={1}
                      borderBottomWidth="1px"
                      borderColor={borderColor}
                    >
                      <Text fontSize="xs" color="gray.600" textAlign="left">
                        {new Date(r.createdAt).toLocaleString()} ·{" "}
                        {r.mode === "detail" ? "Full list" : "Totals"}
                        {r.reportData?.dateFrom || r.reportData?.dateTo
                          ? ` · ${r.reportData.dateFrom || "…"} → ${r.reportData.dateTo || "…"}`
                          : ""}
                      </Text>
                      <HStack spacing={1} flexWrap="wrap">
                        <Button
                          size="xs"
                          colorScheme="teal"
                          variant="outline"
                          onClick={() => openPdfBlobInNewTab(r.blob)}
                        >
                          Open
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() =>
                            downloadPdfBlob(
                              r.blob,
                              `portfolio-saved-${String(r.id).slice(0, 8)}.pdf`
                            )
                          }
                        >
                          Download again
                        </Button>
                        <Button
                          size="xs"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDeleteSavedReport(r.id)}
                        >
                          Remove
                        </Button>
                      </HStack>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}

            <RadioGroup value={analyticsMode} onChange={setAnalyticsMode} mb={3}>
              <HStack spacing={6} flexWrap="wrap" align="flex-start">
                <Radio value="summary">
                  Totals only (per folder)
                </Radio>
                <Radio value="detail">
                  Full list (every row)
                </Radio>
              </HStack>
            </RadioGroup>
            <HStack
              spacing={4}
              flexWrap="wrap"
              align="flex-end"
              mb={3}
              w="full"
            >
              <FormControl maxW={{ base: "full", sm: "200px" }}>
                <FormLabel fontSize="xs" mb={1} textAlign="left">
                  From (optional)
                </FormLabel>
                <Input
                  type="date"
                  size="sm"
                  value={reportDateFrom}
                  onChange={(e) => setReportDateFrom(e.target.value)}
                />
              </FormControl>
              <FormControl maxW={{ base: "full", sm: "200px" }}>
                <FormLabel fontSize="xs" mb={1} textAlign="left">
                  To (optional)
                </FormLabel>
                <Input
                  type="date"
                  size="sm"
                  value={reportDateTo}
                  onChange={(e) => setReportDateTo(e.target.value)}
                />
              </FormControl>
              <HStack spacing={2} pb={1} flexWrap="wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={applyAnalyticsPresetLast7Days}
                >
                  Last 7 days
                </Button>
                <Button size="sm" variant="ghost" onClick={clearAnalyticsDateRange}>
                  Clear dates
                </Button>
              </HStack>
              <Text fontSize="xs" color="gray.500" pb={1} textAlign="left">
                Leave both empty for all time. You can set only one side for an
                open-ended range. Totals use bank/statement dates on rows when those
                are stored.
              </Text>
            </HStack>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              mb={2}
              color="gray.600"
              textAlign="left"
            >
              Which folders
            </Text>
            <VStack
              align="stretch"
              spacing={1}
              maxH="200px"
              overflowY="auto"
              mb={4}
              pr={1}
            >
              {analyticsSubtreeCategories.length === 0 &&
              !analyticsLoadingSubtree ? (
                <Text fontSize="sm" color="gray.500" textAlign="left">
                  No folders here yet.
                </Text>
              ) : (
                analyticsSubtreeCategories.map((c) => {
                  const id = String(c._id);
                  return (
                    <Checkbox
                      key={id}
                      isChecked={analyticsSelectedIds.includes(id)}
                      onChange={() => toggleAnalyticsCategory(id)}
                      colorScheme="purple"
                    >
                      {c.name}
                    </Checkbox>
                  );
                })
              )}
            </VStack>
            <HStack spacing={3} flexWrap="wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={selectAllAnalyticsCategories}
                isDisabled={!analyticsSubtreeCategories.length}
              >
                Select every folder
              </Button>
              <Button
                size="sm"
                colorScheme="purple"
                onClick={handleAnalyticsDownloadPdf}
                isLoading={analyticsPdfBusy}
                loadingText="Working…"
              >
                Download PDF
              </Button>
            </HStack>
          </Box>
          )}

          {subCategories.length !== 0 ||
          subTransactions.length !== 0 ||
          portfolioCombinedMode ? (
            <Flex direction="column" width="100%" gap={6}>
              {/* Subcategories (order=2: below transactions table) */}
              {subCategories.length !== 0 && !portfolioCombinedMode && (
                <Box w="full" mb={6} order={2}>
                  <Box
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={borderColor}
                    overflow="hidden"
                    boxShadow="md"
                    bg={boxBgColor}
                  >
                    <Box
                      px={{ base: 4, md: 6 }}
                      py={4}
                      borderBottomWidth="1px"
                      borderColor={borderColor}
                      bg={breadcrumbBg}
                    >
                      <Flex
                        align="flex-start"
                        justify="space-between"
                        gap={3}
                        flexWrap="wrap"
                      >
                        <Box minW={0}>
                          <Heading
                            size="sm"
                            textAlign="left"
                            color={headingColor}
                            fontWeight="bold"
                          >
                            Subfolders
                          </Heading>
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            mt={1}
                            textAlign="left"
                          >
                            {subCategories.length}{" "}
                            {subCategories.length === 1
                              ? "folder inside"
                              : "folders inside"}{" "}
                            “{selectedCategory.name}”
                          </Text>
                        </Box>
                        <Button
                          size="sm"
                          colorScheme="purple"
                          variant="solid"
                          flexShrink={0}
                          leftIcon={<Icon as={FiList} />}
                          onClick={() =>
                            openCombinedPortfolioView(selectedCategory)
                          }
                        >
                          All nested activity
                        </Button>
                      </Flex>
                    </Box>
                    <Box p={{ base: 4, md: 5 }} bg={categorySectionBodyBg}>
                      <SimpleGrid
                        columns={{ base: 1, md: 2, xl: 3 }}
                        spacing={4}
                        alignItems="stretch"
                      >
                        {subCategories.map((subCategory) => (
                          <PortfolioCategoryFolderCard
                            key={subCategory._id}
                            category={subCategory}
                            variant="blue"
                            collaborationShared={!!subCategory.collaborationShared}
                            isEditingThisRow={
                              isEditing?.id === subCategory._id &&
                              isEditing.type === "category"
                            }
                            editedCategoryName={editedCategoryName}
                            onEditedCategoryNameChange={setEditedCategoryName}
                            onSelect={() =>
                              handleCategorySelect(subCategory)
                            }
                            onSaveEdit={() =>
                              handleCategoryUpdate(
                                subCategory._id,
                                editedCategoryName
                              )
                            }
                            onStartEdit={() =>
                              handleStartEdit(subCategory, "category")
                            }
                            onDelete={handleCategoryDelete}
                            onCombinedBranch={() =>
                              openCombinedPortfolioView(subCategory)
                            }
                            deleteAriaLabel="Delete Subcategory"
                            saveAriaLabel="Save subcategory name"
                            editAriaLabel="Edit subcategory"
                            borderColor={borderColor}
                            textColor={textColor}
                            inputBgColor={inputBgColor}
                            amountColor={amountColor}
                            categoryRowBg={categoryRowBg}
                            categoryRowHoverBg={categoryRowHoverBg}
                            categoryIconBg={categoryIconBgNested}
                            onToggleHideAmountSum={(checked) =>
                              handlePortfolioHideAmountSumToggle(
                                subCategory._id,
                                checked
                              )
                            }
                            hideAmountSumSaving={
                              hideAmountSavingId === String(subCategory._id)
                            }
                          />
                        ))}
                      </SimpleGrid>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Transactions table (order=1: above subcategories) */}
              {(portfolioCombinedMode || subTransactions.length > 0) && (
                <Box
                  w="full"
                  mb={2}
                  order={1}
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor={borderColor}
                  overflow="hidden"
                  boxShadow="md"
                  bg={boxBgColor}
                >
                  <Box
                    px={{ base: 4, md: 6 }}
                    py={4}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                    bg={breadcrumbBg}
                  >
                    <Flex
                      align="flex-start"
                      justify="space-between"
                      gap={3}
                      flexWrap="wrap"
                    >
                      <Box minW={0}>
                        <Heading
                          size="sm"
                          textAlign="left"
                          color={headingColor}
                          fontWeight="bold"
                        >
                          {portfolioCombinedMode
                            ? "All nested transactions"
                            : "Transactions in this folder"}
                        </Heading>
                        <Text
                          fontSize="sm"
                          color="gray.500"
                          mt={1}
                          textAlign="left"
                        >
                          {portfolioCombinedMode && combinedLedgerRootCategory
                            ? `${sortedDisplayTransactions.length} row${
                                sortedDisplayTransactions.length === 1
                                  ? ""
                                  : "s"
                              } from “${
                                combinedLedgerRootCategory.name
                              }” and deeper folders — newest at the top.`
                            : `${sortedDisplayTransactions.length} row${
                                sortedDisplayTransactions.length === 1
                                  ? ""
                                  : "s"
                              } in this folder.`}
                        </Text>
                      </Box>
                      {portfolioCombinedMode && (
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="teal"
                          flexShrink={0}
                          onClick={closeCombinedPortfolioView}
                        >
                          Back to folder cards
                        </Button>
                      )}
                    </Flex>
                  </Box>
                  {portfolioCombinedMode &&
                  combinedLedgerLoading &&
                  combinedLedgerTransactions.length === 0 ? (
                    <Flex justify="center" py={12}>
                      <Spinner size="lg" color="teal.500" />
                    </Flex>
                  ) : (
                  <>
                  <TableContainer
                    maxH={{ base: "none", md: "min(70vh, 560px)" }}
                    overflowY={{ base: "visible", md: "auto" }}
                    overflowX="auto"
                    borderTopWidth="0"
                    sx={{
                      scrollbarGutter: "stable",
                    }}
                  >
                    <Table
                      variant="simple"
                      size="sm"
                      colorScheme="gray"
                      sx={{ borderCollapse: "separate", borderSpacing: 0 }}
                    >
                      <Thead
                        position="sticky"
                        top={0}
                        zIndex={1}
                        bg={tableHeaderBg}
                        backdropFilter="blur(8px)"
                        borderBottomWidth="1px"
                        borderColor={borderColor}
                      >
                        <Tr>
                          <Th
                            py={3}
                            fontSize="xs"
                            fontWeight="semibold"
                            textTransform="uppercase"
                            letterSpacing="0.06em"
                            color="gray.600"
                            borderBottomWidth="2px"
                            borderColor={borderColor}
                          >
                            Description
                          </Th>
                          {portfolioCombinedMode && (
                            <Th
                              py={3}
                              fontSize="xs"
                              fontWeight="semibold"
                              textTransform="uppercase"
                              letterSpacing="0.06em"
                              color="gray.600"
                              borderBottomWidth="2px"
                              borderColor={borderColor}
                              whiteSpace="nowrap"
                            >
                              Folder
                            </Th>
                          )}
                          <Th
                            isNumeric
                            py={3}
                            fontSize="xs"
                            fontWeight="semibold"
                            textTransform="uppercase"
                            letterSpacing="0.06em"
                            color="gray.600"
                            borderBottomWidth="2px"
                            borderColor={borderColor}
                          >
                            Amount
                          </Th>
                          <Th
                            whiteSpace="nowrap"
                            py={3}
                            fontSize="xs"
                            fontWeight="semibold"
                            textTransform="uppercase"
                            letterSpacing="0.06em"
                            color="gray.600"
                            borderBottomWidth="2px"
                            borderColor={borderColor}
                          >
                            Date
                          </Th>
                          <Th
                            w="72px"
                            textAlign="center"
                            py={3}
                            fontSize="xs"
                            fontWeight="semibold"
                            textTransform="uppercase"
                            letterSpacing="0.06em"
                            color="gray.600"
                            borderBottomWidth="2px"
                            borderColor={borderColor}
                          >
                            Docs
                          </Th>
                          <Th
                            textAlign="right"
                            w="104px"
                            py={3}
                            fontSize="xs"
                            fontWeight="semibold"
                            textTransform="uppercase"
                            letterSpacing="0.06em"
                            color="gray.600"
                            borderBottomWidth="2px"
                            borderColor={borderColor}
                          >
                            Actions
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {sortedDisplayTransactions.map((subTransaction) => {
                          const folderLabel =
                            portfolioCombinedMode &&
                            subTransaction.categoryId != null
                              ? combinedCategoryNameById[
                                  String(subTransaction.categoryId)
                                ] ?? "—"
                              : null;
                          const dateDisplay = (() => {
                            const fromSource = [
                              subTransaction.sourceDate,
                              subTransaction.sourceTime,
                            ]
                              .filter(Boolean)
                              .join(" ");
                            if (fromSource) return fromSource;
                            if (subTransaction.date) {
                              try {
                                return format(
                                  new Date(subTransaction.date),
                                  "dd MMMM yyyy h:mm a"
                                );
                              } catch {
                                return "—";
                              }
                            }
                            return "—";
                          })();
                          const cashTone = getPortfolioCashFlowTone(
                            subTransaction
                          );
                          const cashFlowRowBg =
                            cashTone === "out"
                              ? portfolioCashOutRowBg
                              : cashTone === "in"
                                ? portfolioCashInRowBg
                                : neutralTableRowBg;
                          const rowHoverBg =
                            cashTone === "out"
                              ? tableRowHoverOut
                              : cashTone === "in"
                                ? tableRowHoverIn
                                : portfolioRowNeutralHoverBg;
                          const borderLeftColor =
                            cashTone === "out"
                              ? "red.400"
                              : cashTone === "in"
                                ? "green.400"
                                : "transparent";
                          const descText = subTransaction.desc ?? "—";
                          const attachmentUrls = normalizePortfolioTransactionFileUrls(
                            subTransaction.fileUrls
                          );
                          const rowTitle = [
                            descText !== "—" ? `Description: ${descText}` : null,
                            `Amount: ${subTransaction.amount ?? "—"}`,
                            dateDisplay ? `Date: ${dateDisplay}` : null,
                          ]
                            .filter(Boolean)
                            .join(" · ");

                          const isRowEditingTx =
                            isEditing?.id === subTransaction._id &&
                            isEditing.type === "subTransaction";

                          const transactionFolderSelect = (
                            <Select
                              size="sm"
                              value={editedTransactionCategoryId || ""}
                              onChange={(e) =>
                                setEditedTransactionCategoryId(e.target.value)
                              }
                              maxW={{ base: "full", md: "220px" }}
                              bg={inputBgColor}
                            >
                              {editTransactionCategoryOptions.map((o) => (
                                <option key={String(o.id)} value={String(o.id)}>
                                  {o.label}
                                </option>
                              ))}
                            </Select>
                          );

                          return (
                            <Tr
                              key={subTransaction._id}
                              verticalAlign="top"
                              title={rowTitle}
                              bg={cashFlowRowBg}
                              borderLeftWidth="3px"
                              borderLeftColor={borderLeftColor}
                              transition="background 0.15s ease, box-shadow 0.15s ease"
                              cursor="default"
                              _hover={{
                                bg: rowHoverBg,
                                boxShadow: "inset 0 0 0 1px var(--chakra-colors-blackAlpha-50)",
                                "& > td": {
                                  bg: rowHoverBg,
                                },
                              }}
                              sx={{
                                "& > td": {
                                  transition: "background 0.15s ease",
                                  borderBottomWidth: "1px",
                                  borderColor: borderColor,
                                },
                              }}
                            >
                              <Td
                                maxW={{ base: "120px", md: "260px" }}
                                py={3}
                              >
                                {isRowEditingTx ? (
                                  portfolioCombinedMode ? (
                                    <Input
                                      value={editedTransactionDesc}
                                      onChange={(e) =>
                                        setEditedTransactionDesc(e.target.value)
                                      }
                                      size="sm"
                                      variant="outline"
                                      placeholder="Description"
                                      bg={inputBgColor}
                                    />
                                  ) : (
                                    <VStack align="stretch" spacing={2}>
                                      <Input
                                        value={editedTransactionDesc}
                                        onChange={(e) =>
                                          setEditedTransactionDesc(e.target.value)
                                        }
                                        size="sm"
                                        variant="outline"
                                        placeholder="Description"
                                        bg={inputBgColor}
                                      />
                                      {transactionFolderSelect}
                                    </VStack>
                                  )
                                ) : (
                                  <Tooltip
                                    label={descText}
                                    placement="top-start"
                                    openDelay={400}
                                    hasArrow
                                    isDisabled={descText === "—"}
                                  >
                                    <Text
                                      fontSize="sm"
                                      wordBreak="break-word"
                                      color={textColor}
                                      noOfLines={3}
                                    >
                                      {descText}
                                    </Text>
                                  </Tooltip>
                                )}
                              </Td>
                              {portfolioCombinedMode && (
                                <Td
                                  fontSize="sm"
                                  color="gray.600"
                                  py={3}
                                  maxW="140px"
                                  noOfLines={2}
                                  title={folderLabel ?? ""}
                                >
                                  {isRowEditingTx
                                    ? transactionFolderSelect
                                    : folderLabel}
                                </Td>
                              )}
                              <Td isNumeric py={3}>
                                {isRowEditingTx ? (
                                  <Input
                                    value={editedTransactionAmount}
                                    onChange={(e) =>
                                      setEditedTransactionAmount(e.target.value)
                                    }
                                    size="sm"
                                    type="number"
                                    variant="outline"
                                    maxW="112px"
                                    ml="auto"
                                    bg={inputBgColor}
                                    textAlign="right"
                                  />
                                ) : (
                                  <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    fontVariantNumeric="tabular-nums"
                                    color={
                                      cashTone === "out"
                                        ? "red.600"
                                        : cashTone === "in"
                                          ? "green.600"
                                          : amountColor
                                    }
                                  >
                                    {subTransaction.amount}
                                  </Text>
                                )}
                              </Td>
                              <Td
                                fontSize="sm"
                                color="gray.600"
                                whiteSpace="nowrap"
                                py={3}
                              >
                                {dateDisplay || "—"}
                              </Td>
                              <Td textAlign="center" py={3}>
                                {attachmentUrls.length === 0 ? (
                                  <Text fontSize="xs" color="gray.400">
                                    —
                                  </Text>
                                ) : (
                                  <Tooltip
                                    label={`${attachmentUrls.length} supporting document(s)`}
                                    hasArrow
                                  >
                                    <IconButton
                                      aria-label="View supporting documents"
                                      icon={<Icon as={FiPaperclip} />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="purple"
                                      onClick={() => {
                                        setTransactionAttachmentsModalUrls(
                                          attachmentUrls
                                        );
                                        setTransactionAttachmentsModalTitle(
                                          `Documents — ${descText !== "—" ? descText : "Transaction"}`
                                        );
                                        transactionAttachmentsModal.onOpen();
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Td>
                              <Td textAlign="right" py={3}>
                                <HStack spacing={1} justify="flex-end">
                                  {isRowEditingTx ? (
                                    <IconButton
                                      icon={<CheckIcon />}
                                      size="sm"
                                      onClick={() =>
                                        handleTransactionUpdate(
                                          subTransaction._id,
                                          editedTransactionDesc,
                                          editedTransactionAmount,
                                          editedTransactionCategoryId
                                        )
                                      }
                                      colorScheme="green"
                                      aria-label="Confirm Edit"
                                    />
                                  ) : (
                                    <IconButton
                                      icon={<EditIcon />}
                                      size="sm"
                                      onClick={() =>
                                        handleStartEdit(
                                          subTransaction,
                                          "subTransaction"
                                        )
                                      }
                                      colorScheme="blue"
                                      aria-label="Edit transaction"
                                    />
                                  )}

                                  <DeleteButtonWithConfirmation
                                    onDelete={handleTransactionDelete}
                                    itemId={subTransaction._id}
                                    size="sm"
                                    colorScheme="red"
                                    ariaLabel="Delete transaction"
                                  />
                                </HStack>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  <Flex
                    justify="center"
                    align="center"
                    py={3}
                    px={4}
                    borderTopWidth="1px"
                    borderColor={borderColor}
                    gap={3}
                    flexWrap="wrap"
                  >
                    {portfolioCombinedMode &&
                      combinedLedgerTotal > 0 && (
                        <Text fontSize="xs" color="gray.500">
                          Showing {combinedLedgerTransactions.length} of{" "}
                          {combinedLedgerTotal}
                        </Text>
                      )}
                    {!portfolioCombinedMode && subTransactionsTotal > 0 && (
                      <Text fontSize="xs" color="gray.500">
                        Showing {subTransactions.length} of {subTransactionsTotal}
                      </Text>
                    )}
                    {portfolioCombinedMode &&
                      combinedLedgerPage < combinedLedgerTotalPages && (
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="purple"
                          onClick={loadMoreCombinedLedger}
                          isLoading={
                            combinedLedgerLoading &&
                            combinedLedgerTransactions.length > 0
                          }
                        >
                          Show more
                        </Button>
                      )}
                    {!portfolioCombinedMode &&
                      subTransactionsPage < subTransactionsTotalPages && (
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="teal"
                          onClick={loadMoreSubTransactions}
                        >
                          Show more
                        </Button>
                      )}
                  </Flex>
                  </>
                  )}
                </Box>
              )}
            </Flex>
          ) : (
            <Text>No Data available</Text>
          )}
        </>
      )}
        </VStack>
      </Container>
    </Box>
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => {
          if (!isImportingCsv) {
            closeImportModal();
          }
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Import Portfolio CSV</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Top Parent Category Name</FormLabel>
                <Input
                  value={importParentCategoryName}
                  onChange={(e) => setImportParentCategoryName(e.target.value)}
                  placeholder="e.g. Anup's Investment"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>CSV File</FormLabel>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  p={1}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={closeImportModal} isDisabled={isImportingCsv}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleImportPortfolioCsv}
              isLoading={isImportingCsv}
            >
              Import
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={transactionAttachmentsModal.isOpen}
        onClose={transactionAttachmentsModal.onClose}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md" pr={10}>
            {transactionAttachmentsModalTitle}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={3}>
              {transactionAttachmentsModalUrls.map((url, idx) => (
                <Link
                  key={`${url}-${idx}`}
                  href={url}
                  isExternal
                  color="blue.500"
                  wordBreak="break-all"
                  fontSize="sm"
                >
                  {portfolioSupportingDocumentDisplayName(url, idx)}
                </Link>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
      <AlertDialog
        isOpen={isSaveReportDialogOpen}
        leastDestructiveRef={cancelSaveReportRef}
        onClose={skipSaveReport}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Save this report on your device?
            </AlertDialogHeader>
            <AlertDialogBody>
              Keep a copy in this browser so you can open it from Saved reports
              later without running the report again. It does not leave your
              computer.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelSaveReportRef} onClick={skipSaveReport}>
                No thanks
              </Button>
              <Button
                colorScheme="purple"
                onClick={confirmSaveReportToDevice}
                ml={3}
              >
                Save copy
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <AlertDialog
        isOpen={isForceDeleteDialogOpen}
        leastDestructiveRef={cancelForceDeleteRef}
        onClose={() => {
          setPendingForceDeleteCategoryId(null);
          closeForceDeleteDialog();
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Category With Associated Items
            </AlertDialogHeader>
            <AlertDialogBody>
              This category has associated items. Do you want to delete this
              category and all associated nested items as well?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelForceDeleteRef}
                onClick={() => {
                  setPendingForceDeleteCategoryId(null);
                  closeForceDeleteDialog();
                }}
              >
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleForceDeleteCategory} ml={3}>
                Delete All Associated Items
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <ShareCollaborationModal
        isOpen={sharePortfolioModal.isOpen}
        onClose={() => {
          sharePortfolioModal.onClose();
          setSharePortfolioCategory(null);
        }}
        titleLabel={
          sharePortfolioCategory
            ? `Share portfolio folder “${sharePortfolioCategory.name}”`
            : "Share folder"
        }
        kind="PORTFOLIO_CATEGORY"
        anchor={{
          categoryAnchorId: sharePortfolioCategory
            ? String(sharePortfolioCategory._id)
            : undefined,
        }}
      />
    </>
  );
};

export default PortfolioManager;
